/**
 * Foundry plant fixture — the canonical demo plant model.
 *
 * Ported from `D:\WORK\SAHAS\Projects\Digital Twin Concept\app\src\data\foundryTemplate.ts`,
 * with entity references pointing at our pack YAML templates in
 * packs/foundry/entities/*.yaml.
 *
 * The plant has 17 entity instances + 19 connections covering the full
 * grey iron foundry workflow including the sand-testing branch and the
 * return-sand recirculation loop.
 */

import type { LucideIcon } from "lucide-react";
import {
  ClipboardCheck,
  Cog,
  Flame,
  Layers,
  Microscope,
  Truck,
} from "lucide-react";
import type { BehaviorGraph } from "@/lib/plant/behavior";
import type { OperatingModes } from "@/lib/plant/operating-modes";

export type EntityClass = "STATION" | "EQUIPMENT" | "INSTRUMENT" | "TRANSPORT";
export type LotOp = "MERGE" | "TRANSFORM" | "SPLIT" | "PASSTHROUGH";
export type IconKey = "flame" | "cog" | "microscope" | "truck" | "clipboard" | "layers";

/**
 * The data-kind buckets that classify what a tag / port / parameter
 * actually emits. Used by the dashboard editor to filter widget bindings
 * (e.g., a histogram needs a numeric scalar; a Pareto needs a categorical
 * distribution). Mirrors the `data_kind:` field on semantic-tag YAML and
 * entity sensor / parameter YAML.
 *
 * See docs/06 § dashboard widgets — data-kind taxonomy.
 */
export type DataKind =
  | "analog_scalar"           // bounded numeric continuous (bath_temp, pour_temp)
  | "analog_percentage"       // ratio / percent in ~[0, 100] (sand_moisture, oee)
  | "count_scalar"            // non-negative integer (mould_count, defect_count)
  | "derived_kpi"             // pre-computed composite (oee, carbon_equivalent)
  | "categorical_enum"        // small discrete label set (shift, grade)
  | "categorical_distribution"// named buckets with counts (defects by type)
  | "event_stream"            // timestamped discrete events (alarms, batches)
  | "boolean_state"           // on/off, healthy/degraded
  | "string_label"            // free-form / identifier text (heat_number)
  | "image";                  // captured photo — binary content, not numeric

export type MaterialClass =
  | "ferrous_metal"
  | "ferrous_charge"
  | "pig_iron"
  | "steel_scrap"
  | "foundry_return"
  | "casting_rejects"
  | "molten_iron"
  | "molten_steel"
  | "molten_aluminium"
  | "molten_copper"
  | "molten_zinc"
  | "green_sand"
  | "return_sand"
  | "silica_sand"
  | "green_sand_mould"
  | "no_bake_mould"
  | "permanent_mould"
  | "investment_shell"
  | "poured_mould"
  | "cooled_mould"
  | "raw_casting"
  | "finished_casting"
  | "test_specimen"
  | "pattern"
  | "waste"
  | "alloy_material"
  | "water"
  | "bentonite"
  // Standard green-sand foundry additions (STD archetype).
  | "sea_coal"
  | "core_sand"
  | "resin_pur"
  | "amine_catalyst"
  | "cold_box_core"
  // No-bake self-setting binder system (#699) — chemically distinct from the
  // cold-box PUR/amine pair above (gas-cured in the shooter): no-bake resin
  // cures in place from a liquid catalyst mixed in at the continuous mixer,
  // no external gas step. Named to match the existing `no_bake_mould` class.
  | "no_bake_resin"
  | "no_bake_catalyst";

/**
 * Resolve a material class to a CSS color (drives port handles + edge
 * stroke). Tolerant of undefined (energy ports carry no material class)
 * → falls back to a neutral muted tone.
 */
export function materialColor(mc?: MaterialClass): string {
  if (!mc) return "rgb(var(--color-on-surface-muted))";
  return `rgb(var(--color-material-${mc.replace(/_/g, "-")}))`;
}

/**
 * PROCESS LINES — the swim-lane grouping the process graph draws (Phase 2),
 * mirroring the reference plant's core / sand / metal grouping. `order` sets the
 * vertical lane order (top → bottom); `tint` is the faint band background
 * (reference palette at low alpha so it never fights the edges/nodes).
 */
export type ProcessLine =
  | "melt"
  | "sand"
  | "core"
  | "mould"
  | "finishing"
  | "lab"
  | "tooling";

export const PROCESS_LINE_INFO: Record<
  ProcessLine,
  { label: string; order: number; tint: string }
> = {
  melt: { label: "Melt & Metal", order: 0, tint: "rgba(244,105,95,0.06)" },
  sand: { label: "Sand Plant", order: 1, tint: "rgba(217,179,130,0.08)" },
  core: { label: "Core Shop", order: 2, tint: "rgba(167,139,250,0.06)" },
  mould: { label: "Moulding & Pour", order: 3, tint: "rgba(74,222,128,0.06)" },
  finishing: { label: "Finishing & QC", order: 4, tint: "rgba(96,165,250,0.06)" },
  lab: { label: "Labs", order: 5, tint: "rgba(56,198,217,0.06)" },
  tooling: { label: "Tooling", order: 6, tint: "rgba(143,166,196,0.07)" },
};

// Tooling is read off the template id alone (H10, 2026-10-24): a one-liner that says what a machine works with
// ("green sand and a pattern make moulds") filed the Moulding Line under Tooling. A corebox or pattern template is the
// tool itself.
const TOOLING_TEMPLATE = /corebox|pattern/;

// Ordered templateId / one-line keyword → line rules (FIRST match wins), after the tooling test above.
// Precedence matters: auto-pour/inoc→melt before a bare "pour"→mould; "sand cooler"→sand before generic.
const LINE_RULES: Array<[RegExp, ProcessLine]> = [
  [/sand[_ -]?lab|core[_ -]?lab|\blab\b|vcat|tensile|spectro|\boes\b|hardness[_ -]?test|moisture[_ -]?test/, "lab"],
  [/charge|furnace|induction|ladle|treat|inoc|auto[_ -]?pour|pig[_ -]?iron|scrap|ferro|alloy|recarb|addit/, "melt"],
  [/muller|sand[_ -]?mix|bentonite|sea[_ -]?coal|\bcoal\b|fresh[_ -]?sand|water[_ -]?add|batch[_ -]?hopper|bunker|cooler|sieve|lump|magnet|dust|return[_ -]?sand|compact/, "sand"],
  [/resin|shooter|blower|coating|\boven\b|core[_ -]?knock|coreko|\bcore\b/, "core"],
  [/mould|disa|core[_ -]?set|coreset|\bpour|cooling|shakeout|knockout/, "mould"],
  [/blast|de[_ -]?gat|fettl|grind|finish|final|inspect|\bndt\b|\bstore\b|dispatch|reject|runner|riser/, "finishing"],
];

/**
 * Derive an entity's PROCESS LINE for swim-lane grouping. Uses the explicit
 * {@link EntityDef.line} override when set; else matches templateId / one-line
 * keywords (ordered, first-match-wins); else falls back to its primary material
 * class; else the ontology class (INSTRUMENT → lab). Defaults to "mould" (the
 * plant's spine) so an unrecognised station still lands in a lane, not nowhere.
 */
export function lineForEntity(def: EntityDef): ProcessLine {
  if (def.line) return def.line;
  // A stored blob may lack a field (lib/api/twin-entities.ts): a def with no template id still gets a lane.
  const templateId = (def.templateId ?? "").toLowerCase();
  if (TOOLING_TEMPLATE.test(templateId)) return "tooling";
  const key = `${templateId} ${def.oneLine ?? ""}`.toLowerCase();
  for (const [re, line] of LINE_RULES) if (re.test(key)) return line;
  const mats = [...(def.outputPorts ?? []), ...(def.inputPorts ?? [])].map((p) => p.materialClass);
  if (mats.some((m) => m === "molten_iron" || m === "ferrous_charge" || m === "ferrous_metal")) return "melt";
  if (mats.some((m) => m === "green_sand" || m === "return_sand" || m === "silica_sand" || m === "bentonite")) return "sand";
  if (mats.some((m) => m === "cold_box_core" || m === "core_sand" || m === "resin_pur" || m === "amine_catalyst")) return "core";
  if (mats.some((m) => m === "raw_casting" || m === "finished_casting")) return "finishing";
  if (def.class === "INSTRUMENT") return "lab";
  return "mould";
}

/**
 * Energy classes — the seed taxonomy for energy ports + energy flow edges
 * (P2.1). An entity's energy ports declare what utility it draws/emits
 * (electrical power in, thermal/steam in, etc.). Distinct from material
 * flow so the process graph can render + legend them separately.
 * Mirrors packs/foundry/energy/classes.yaml; server carries it on
 * entity_connections.energy_class (migration 0033).
 */
export type EnergyClass =
  | "electrical"
  | "thermal"
  | "compressed_air"
  | "natural_gas"
  | "steam"
  | "hydraulic";

export interface EnergyClassInfo {
  label: string;
  /** Distinct hue for graph edges / port handles / legend swatches. */
  color: string;
  /** Conventional SI-ish display unit for this utility. */
  unit: string;
}

export const ENERGY_CLASS_INFO: Record<EnergyClass, EnergyClassInfo> = {
  electrical: { label: "Electrical", color: "#f59e0b", unit: "kW" },
  thermal: { label: "Thermal", color: "#ef4444", unit: "kW" },
  compressed_air: { label: "Compressed air", color: "#38bdf8", unit: "Nm³/h" },
  natural_gas: { label: "Natural gas", color: "#84cc16", unit: "m³/h" },
  steam: { label: "Steam", color: "#a78bfa", unit: "kg/h" },
  hydraulic: { label: "Hydraulic", color: "#14b8a6", unit: "bar" },
};

export const ENERGY_CLASSES = Object.keys(ENERGY_CLASS_INFO) as EnergyClass[];

/** Resolve an energy class to its display color (drives energy port handles + edge stroke). */
export function energyColor(ec: EnergyClass): string {
  return ENERGY_CLASS_INFO[ec]?.color ?? "rgb(var(--color-on-surface-muted))";
}

export interface PortDef {
  id: string;
  name: string;
  /**
   * Material class for material ports. Optional because energy ports
   * carry an `energyClass` instead (a port is material XOR energy).
   * Material ports always set this; the foundry fixture below does too.
   */
  materialClass?: MaterialClass;
  /**
   * Energy class for energy ports (electrical / thermal / steam / …).
   * Mutually exclusive with materialClass — set on energy-in/-out ports
   * authored in the entity builder (P2.3).
   */
  energyClass?: EnergyClass;
  /**
   * What the sensor / output emits on this port, if it streams telemetry.
   * Drives widget-binding compatibility in the dashboard editor.
   * Ports that only carry material (no sensor) leave this undefined.
   */
  dataKind?: DataKind;
  /**
   * ISA-88 flow mode of the material crossing this port (M8.5). Promoted
   * to first-class from the pack YAML (`packs/foundry/entities/*.yaml`
   * per-port `flow_mode`). Drives the mass-balance engine's handler
   * selection (batch vs continuous vs discrete-unit) + process-graph
   * throughput badges. Optional — energy/sensor ports leave it undefined.
   */
  flowMode?: FlowMode;
}

/**
 * ISA-88 flow taxonomy — how material crosses a port / how a station runs.
 * Mirrors the per-port `flow_mode` in pack YAML + the server's
 * `lot_operations.flow_mode` CHECK + `internal/massbalance.FlowMode`.
 */
export type FlowMode = "batch" | "continuous" | "discrete_unit";

/**
 * A nameplate / rated physical attribute of an entity (M8.5) — capacity,
 * rated power, cooling time, etc. Promoted to first-class from the pack
 * YAML `properties:` block so the mass-balance engine + capacity/throughput
 * badges can read them off the EntityDef. `value` is in `unit`.
 */
export interface RatedProperty {
  name: string;
  value: number;
  unit?: string;
}

/** Provenance state of a rated (nameplate) field — Phase-2 0.C. */
export type RatedFieldTierState = "template_default" | "confirmed";

/**
 * Per-field provenance stamp for a rated property (Phase-2 0.C). `tier` is
 * `"template_default"` (an unconfirmed catalog value — rendered amber)
 * everywhere this record is ABSENT; `"confirmed"` (rendered green) is set ONLY
 * by an explicit human confirmation at commissioning, never inferred — so a
 * template default can never masquerade as a measured/vouched value.
 * `confirmed_by` / `confirmed_at` record who vouched + when (the audit trail
 * the S4.1 provisioning flip writes; the flip itself lands with the survey
 * checklist, not here).
 */
export interface RatedFieldTier {
  tier: RatedFieldTierState;
  confirmed_by?: string;
  confirmed_at?: string;
}

/** A port is material XOR energy; default to material when ambiguous. */
export function portKind(p: { materialClass?: MaterialClass; energyClass?: EnergyClass }): "material" | "energy" {
  return p.energyClass ? "energy" : "material";
}

/**
 * How a data-port parameter's `value` came to exist (S2.3b, 2026-08-03). The two
 * member strings are taken VERBATIM from the closed provenance vocabulary in
 * `platform-core/internal/twin/provenance.go` (`ProvTemplateDefault`,
 * `ProvCaptured`) and a Go static gate asserts this union stays a subset of it —
 * a third spelling of "a catalog constant" is how a constant launders itself into
 * a measurement, which is the whole subject of S3.3's vocabulary.
 *
 *   - `"TEMPLATE_DEFAULT"` — a catalog/template constant this plant never
 *     confirmed. Every value the shipped templates seed is one of these.
 *   - `"CAPTURED"` — a human entered/declared it (the config panel's value
 *     commit).
 *
 * ABSENT is not a third state and is not "unknown": it is read as
 * `TEMPLATE_DEFAULT`, which is exact rather than cautious on every row that
 * exists. Measured on staging 2026-08-03 across all 13 orgs: of 679 persisted
 * parameters, 674 carry no `valueSource` at all and the 5 that do are ALSO
 * template-authored (4 × `water_dose_setpoint` shipped `valueSource:"manual"` by
 * `catalog/stages.ts`, 1 × a valueless orphan) — i.e. there is not one
 * human-entered data-port value anywhere, because the only writers of a
 * parameter value are the catalog templates and `EntityConfigPanel.commitValue`.
 */
export type ParamValueProvenance = "TEMPLATE_DEFAULT" | "CAPTURED";

/**
 * What a parameter measures or controls — contract K2 in
 * docs/design/2026-09-24_analytics-contracts.md. The per-plant analysis planner
 * reads it to place a tag in its families (chemistry against outcomes, sand-system
 * process × process, setpoint tracking); a parameter without it falls back to a
 * name-based guess. Named `paramClass` (L1: `param_class`) so it cannot be confused
 * with the template-level `class` or the pack merge's `measurement_class`.
 */
export type ParamClass =
  | "chemistry"      // composition of a melt or sample (%)
  | "temperature"    // a measured temperature
  | "sand_property"  // a green-sand property (moisture, compactibility, strength, clay, LOI)
  | "setpoint"       // a target an operator or controller sets — pair it with `setpointOf`
  | "state"          // an on/off or mode signal
  | "rate"           // a flow, speed or throughput per unit time
  | "count"          // a count of things
  | "other";

export interface ParameterDef {
  name: string;
  unit?: string;
  value?: number | string;
  range?: [number, number];
  /**
   * Provenance of `value` — see {@link ParamValueProvenance}. Written ONLY by the
   * path that writes a VALUE (`EntityConfigPanel`'s `commitValue`); picking a
   * `valueSource` mode deliberately does NOT stamp it, because clicking "Manual"
   * changes who will supply the number later, not who supplied the one on screen.
   * Conflating the two is precisely how `valueSource` fails as a provenance proxy.
   */
  valueProvenance?: ParamValueProvenance;
  /** What this parameter measures or controls (contract K2) — see {@link ParamClass}. */
  paramClass?: ParamClass;
  /**
   * For a setpoint: the name of the measured parameter on the same entity that it
   * targets (contract K2), e.g. `compactibility_setpoint` → `compactibility`. Setpoint
   * tracking pairs the two tags from this, instead of guessing from names.
   */
  setpointOf?: string;
  /**
   * Classification of what this parameter emits. Required for any
   * parameter that should be bindable in a dashboard widget.
   */
  dataKind?: DataKind;
  /**
   * Where this parameter's value comes from (material-genealogy-real C2 — the
   * "data port" model). Optional + additive — absent ⇒ treated as `"fixed"`:
   *   - `"fixed"`  → use `value` as authored now (demo / static datum).
   *   - `"bound"`  → resolved from an `entity_tag_bindings` row later (FE-A
   *                  EntityConfigPanel binds a tag); falls back to `value`
   *                  until a tag is wired.
   *   - `"manual"` → operator-entered at run time.
   * See docs/design/material-genealogy-real.md § Per-equipment granularity.
   */
  valueSource?: "fixed" | "manual" | "bound";
  /**
   * Which operating modes this parameter is measured in (Phase-1 S1 / W4) — the
   * per-parameter counterpart of `operating_modes`, referencing that model's mode
   * names. The simulator emits this parameter's tag only while the equipment is in
   * one of these modes (so no bath_temperature streams in idle). Rides
   * `data.entity.parameters[]` through the entity upsert, next to
   * `data.entity.operating_modes`. ABSENT or EMPTY ⇒ active in EVERY mode (the
   * always-on default), matching the simulator's `active_modes` convention.
   * Promoted from simulator-catalog-only to platform storage so a plant authors it
   * in the portal.
   */
  active_modes?: string[];
}

/**
 * A boundary signal a station emits (M8.9) — a PLC-style trigger that marks
 * when an operation starts/ends or pulses once per produced unit. Declared on
 * the EntityDef so authoring (M9) can bind a real tag to it; the boundary
 * detection engine (M8.9.3) then sources operation timing from the bound tag
 * instead of the cosmetic offsets, falling back to the rate model when nothing
 * is bound. `dataKind` drives AutoSuggest tag matching (a trigger bit is a
 * boolean_state; a per-unit pulse is a count_scalar; a level-crossing is an
 * analog_scalar). Mirrors entity_tag_bindings.purpose (migration 0040).
 */
export interface BoundarySignal {
  id: string;
  name: string;
  purpose: "cycle_start" | "cycle_end" | "pulse" | "threshold";
  dataKind?: DataKind;
}

/**
 * Per-entity output-tracking config (P1.8b) — declares how an entity's primary
 * output is recorded as an identified, QC'd "stage". Lives on the EntityDef so
 * it round-trips through `data.entity.trackedOutput` (plantNodeToCreate); the
 * server's GET /v1/twin/stages derives one Stage per producing entity from it.
 * Mirrors packs/foundry/output-stages.yaml.
 *
 * `tracked: false` (or omitting the whole object) means the output is not
 * tracked as a discrete unit. `isPart` gates whether the stage shows in the
 * Parts screen's stage selector. The output's material class is read from the
 * entity's first output port (`outputPorts[0].materialClass`).
 */
export interface TrackedOutput {
  tracked: boolean;
  /** Human-readable, heat-scoped id prefix for serialized units (e.g. "MO"). */
  idPrefix: string;
  /** Stage label shown in the Parts selector (e.g. "Mould"). */
  label: string;
  /** Surface this stage in the Parts UI stage selector. */
  isPart: boolean;
  /** The measurement attached to the output + the defects it can reveal. */
  qc: { tests: string[]; defects: string[] };
}

export interface EntityDef {
  id: string;
  templateId: string;
  class: EntityClass;
  lotOp: LotOp;
  iconKey: IconKey;
  oneLine: string;
  /**
   * Optional catalog grouping tag (e.g. "STD" for the Standard Green-Sand
   * Foundry equipment set). Surfaced as a small badge on the add-entity
   * catalog card so builders know an entity belongs to a curated set.
   * Additive — entities without it render no badge.
   */
  category?: string;
  /**
   * Which PROCESS LINE this station belongs to (melt / sand / core / mould /
   * finishing / lab / tooling) — drives the grouped "swim-lane" bands in the
   * process graph (Phase 2, mirroring the reference plant's core/sand/metal
   * grouping). Optional: when absent, {@link lineForEntity} derives it from the
   * templateId / ontology class / material classes, so existing archetypes and
   * user-added entities group correctly without being hand-tagged. Set it only
   * to OVERRIDE the derivation.
   */
  line?: ProcessLine;
  inputPorts: PortDef[];
  outputPorts: PortDef[];
  parameters: ParameterDef[];
  /**
   * Capture marker: this instrument's readings arrive from a PHONE over BLE, not
   * from a sensor. PRESENCE is the discriminator — an entity carrying bleModel gets
   * NO gateway Thing from generatePlant(), because a simulated gateway would
   * fabricate readings that overwrite the operator's real ones. plants_generate.go
   * instead provisions its tag on the plant's manual-entry gateway with
   * source='ble' (it MUST live there: my-manual-entry-targets inner-joins
   * product_id='manual-entry-app-gateway', so a tag anywhere else is invisible to
   * the app).
   *
   * The value only selects the row in the SERVER's catalog (twin.handheldCatalog),
   * which supplies unit/range/semantic_tag/frame_field — none of that is duplicated
   * here. One field rather than captureMode+model: two fields can disagree, and
   * presence already carries the boolean.
   */
  bleModel?: "VCH" | "VMH" | "VMS";
  /**
   * Feed marker: this instrument's results arrive from its own app through the gateway the site commissions (the
   * driver named here), and Connect creates and binds its tags at go-live. PRESENCE is the discriminator, as with
   * bleModel: an entity carrying it gets NO gateway Thing from generatePlant(), so no empty template tag sits on the
   * ports Connect binds (two primary tags on one port, and a board's name lookup finds the empty one).
   */
  feedDriver?: "results-feed";
  /**
   * Output-tracking config (P1.8b) — how this entity's output is identified +
   * QC'd as a foundry stage. Optional + additive; entities without it produce
   * untracked outputs (no Stage row). See {@link TrackedOutput}.
   */
  trackedOutput?: TrackedOutput;
  /**
   * Nameplate / rated physical attributes (M8.5) — capacity_kg,
   * rated_power_kw, cooling_time, etc. Promoted to first-class from pack
   * YAML; feeds the mass-balance engine + capacity/throughput badges.
   * Optional + additive — existing fixtures without it keep working.
   */
  ratedProperties?: RatedProperty[];
  /**
   * Per-rated-field provenance tiers (Phase-2 0.C, P2). Keyed by the rated
   * field's `name` (a STATIC_RATED_NAMES const, e.g. "capacity_kg"). ABSENT ⇒
   * the value is an unconfirmed template default (the config panel renders it
   * as the amber "template default" chip). A field is only "confirmed" (green)
   * when an explicit `{tier:"confirmed"}` entry says a human vouched for the
   * nameplate — the panel NEVER infers confirmation, so a template default can
   * never masquerade as a measurement. Optional + additive; the commissioning
   * flip (amber → green via the S4.1 provisioning endpoint) lands with the
   * survey checklist, not with the panel re-cut.
   */
  ratedTiers?: Record<string, RatedFieldTier>;
  /**
   * Machine-named physics priors (S02 §2a) — the equipment doc's priors
   * table (eta_el, dh_melt, loss_melt_w, …). Same {name, value, unit} shape
   * as ratedProperties; rides `data.entity.priors`. The server bridge
   * (twin.BuildSimcoreProps) maps BOTH lists into the simcore expr
   * environment, priors OVERLAYING ratedProperties on a name collision, with
   * unit→SI conversion applied once at load (kJ→J, kW→W, %→fraction; SI /
   * unlisted units pass through). Only meaningful alongside `behavior`.
   */
  priors?: RatedProperty[];
  /**
   * Default station-level flow mode (M8.5) — the dominant ISA-88 mode the
   * station runs in (batch furnace, continuous DISA, FIFO-cooling line).
   * Per-port `flowMode` on outputPorts refines it where they differ.
   */
  flowMode?: FlowMode;
  /**
   * Boundary signals this station emits (M8.9) — PLC triggers that mark
   * operation start/end or pulse per produced unit. Authoring binds a real tag
   * to each; the detection engine sources operation timing from them. Optional
   * + additive (templates without it keep the rate-modeled timing).
   */
  boundarySignals?: BoundarySignal[];
  /**
   * Physics-twin behaviour contract (E1) — the S02 behaviour graph (state /
   * ports / blocks / outputs). Rides `data.entity.behavior` exactly like
   * `trackedOutput` does — additive, zero DDL. Absent ⇒ the entity is v1
   * (shadow-only); present ⇒ physics-enabled. See
   * docs/physics-twin-build/S02-behaviour-contract.md.
   */
  behavior?: BehaviorGraph;
  /**
   * The simcore library version the behaviour graph was authored against
   * (S05 §5). Only meaningful when `behavior` is present.
   */
  behaviorVersion?: number;
  /**
   * Declared operating-mode model (Phase-1 S1) — the per-equipment FSM (modes,
   * current_mode, transitions = events + triggers) the platform reads to derive
   * `equipment_state` (basis `entity_view`). Rides `data.entity.operating_modes`
   * as a SIBLING of `behavior`, but UNGATED (D1): operational state is read for
   * every org, not only physics-enabled ones. Shipped by the pack injection and
   * round-trips through the entity upsert; the portal editor (W4) edits it in
   * place. Absent ⇒ no declared modes (the platform uses the coarse global map).
   */
  operating_modes?: OperatingModes;
}

export const ICON_MAP: Record<IconKey, LucideIcon> = {
  flame: Flame,
  cog: Cog,
  microscope: Microscope,
  truck: Truck,
  clipboard: ClipboardCheck,
  layers: Layers,
};

export interface PlantNode {
  id: string;
  instanceName: string;
  position: { x: number; y: number };
  entity: EntityDef;
}

export interface PlantEdge {
  id: string;
  source: string;
  target: string;
  sourceHandle: string;
  targetHandle: string;
  label: string;
  /** Material class for material flows. Optional — energy edges set energyClass. */
  materialClass?: MaterialClass;
  /** Energy class for energy flows (electrical/thermal/steam/…). Mutually exclusive with materialClass. */
  energyClass?: EnergyClass;
  /** "dashed" for feedback loops / non-process flows. */
  style?: "solid" | "dashed";
}

// --- Entity definitions (mirrors packs/foundry/entities/*.yaml) -------

const ENT_CHARGE_PREP: EntityDef = {
  id: "ent-charge-prep",
  templateId: "foundry.charge_preparation_bay",
  class: "STATION",
  lotOp: "MERGE",
  iconKey: "layers",
  oneLine: "Weighs pig iron, scrap and alloys into a batch, the furnace charge.",
  ratedProperties: [{ name: "capacity_kg", value: 2000, unit: "kg" }],
  inputPorts: [
    // ADR 1b (#677): in_pig/in_scrap carry the SPLIT classes (bought,
    // purchased-consumption economics), not the legacy ferrous_metal
    // umbrella. in_pig stays inputPorts[0] — the runtime reads Ports[0] for
    // InMaterial, so its position is load-bearing.
    { id: "in_pig", name: "pig_iron", materialClass: "pig_iron" },
    { id: "in_scrap", name: "steel_scrap", materialClass: "steel_scrap" },
    { id: "in_alloy", name: "alloy_additions", materialClass: "alloy_material" },
    // NEW ports (ADR 1b: new identities, not renames of in_pig/in_scrap) —
    // internal returns (degating runners/risers, final-inspection rejects)
    // land here, carrying internal-return economics instead of masquerading
    // as bought material on in_scrap.
    { id: "in_returns", name: "foundry_returns", materialClass: "foundry_return" },
    { id: "in_rejects", name: "casting_rejects", materialClass: "casting_rejects" },
  ],
  outputPorts: [{ id: "out_charge", name: "furnace_charge", materialClass: "ferrous_charge" }],
  // WS-B#2 (2026-08-02) — this bay used to ship THREE parameters:
  //   charge_weight 2150 kg [1800-2500] · pig_iron_pct 70 % [65-75] · scrap_pct 30 % [25-35]
  // and the emitter synthesised all three at random onto the charge MERGE op —
  // the same op whose output_qty is the real charge (2000 kg, from capacity_kg)
  // and whose INPUT EDGES already carry the real make-up (pig_iron 45 %). Two
  // numbers for one quantity on one operation, and the random one wore the spec
  // range + inSpec flag that make a number look measured.
  //
  // The dossier settles both halves (decisions/2026-07/equipment-dossier/01-melt-line.md):
  //   :78  "E1-forbidden defaults to DELETE from the equipment … invented recipe
  //         fractions on the equipment … Charge make-up is metallurgy that changes
  //         every heat; it is not a nameplate property."
  //   :113 "Charge mass … are [STREAM] (weighbridge) — never hand-set. The
  //         pig/scrap % must be RECIPE, never an equipment default."
  //
  // So the make-up fractions are GONE (they are recipe setpoints — Layer 4 — and
  // the panel now renders them "—" under the Recipe home instead of a fabricated
  // 70/30), and charge_weight survives as exactly what Layer 2 lists: a bin
  // loadcell, kg, 0-6000, "the mass anchor". It carries NO nominal `value` — a
  // weighed charge is a per-heat MEASUREMENT, so any number here would be the
  // equipment asserting a recipe number as its own nameplate. Keeping the PORT
  // matters: generatePlant turns parameters into gateway tags, and `charge_weight`
  // is the tag the real-capture mass fusion binds for ferrous_charge
  // (platform-core/internal/twin/mass_fusion.go:104).
  //
  // The emitter no longer draws it either — emitCharge DERIVES the chip from the
  // charge it actually made (genealogy_newmodel.go, resolveChargePortChips), and
  // WITHHOLDS any make-up percentage a customer re-adds by hand.
  parameters: [
    { name: "charge_weight", unit: "kg", range: [0, 6000], dataKind: "analog_scalar" },
  ],
  boundarySignals: [
    { id: "sig_charge_done", name: "charge_complete", purpose: "cycle_end", dataKind: "boolean_state" },
  ],
};

/**
 * The furnace behaviour graph (physics twin, slice BT-5) — byte-faithful to
 * the box-verified canonical graph in
 * platform-core/internal/simcore/furnace_test.go (furnaceGraphJSON), which is
 * the equipment/02-furnace.md · S02 §2 canonical example PLUS the documented
 * melt-complete handoff: the meltdown→superheat transition emits
 * `melt_complete` and the event-stepped `meltset` block writes the melt
 * temperature (1150 °C) into bath_temp on it — without this, superheat
 * integrates from the cold IC and 1500 °C arrives ~2300 s late.
 *
 * Solved against ENT_FURNACE.priors below (SI at load, S03 §2); verified by
 * the S06 goldens (furnace_test.go) + the operating-envelope suite
 * (furnace_envelope_test.go). `corrected_by.tag` is the bench's placeholder
 * sensor name — estimation mode rebinds it per plant at tag-bind time;
 * simulation ignores it (S02 §3).
 */
const FURNACE_BEHAVIOR: BehaviorGraph = {
  state: [
    { name: "bath_temp", unit: "C", init: 25, min: 0, max: 1600,
      corrected_by: { tag: "furnace1.bath_temp", tier: "METERED" } },
    { name: "x_melt", unit: "", init: 0, min: 0, max: 1 },
    { name: "C", unit: "%", init: 3.4 },
    { name: "Si", unit: "%", init: 2.0 },
  ],
  ports: {
    data: [
      { name: "power_kw", unit: "kW" },
      { name: "tilt_angle", unit: "deg" },
      { name: "ambient", unit: "C" },
    ],
    material: [
      { name: "charge_in", dir: "in", class: "ferrous_charge" },
      { name: "molten_out", dir: "out", class: "molten_iron" },
    ],
    manual: [{ name: "tap_temp_dip", unit: "C" }],
    trigger: [
      { name: "charge_complete", dir: "in" },
      { name: "tap", dir: "out" },
    ],
    energy: [{ name: "power_in", class: "electrical_3ph" }],
  },
  blocks: [
    { id: "phase", prim: "phase_machine",
      params: { phases: ["meltdown", "superheat", "hold", "tap_p"],
        transitions: [
          { from: "meltdown", to: "superheat", when: "x_melt >= 1", emit: "melt_complete" },
          { from: "superheat", to: "hold", when: "bath_temp >= t_set" },
          { from: "hold", to: "tap_p", when: "tilt_angle > 10" },
        ] } },
    { id: "cond", prim: "loss", in: { s: "@bath_temp" },
      params: { mode: "linear", k: 30, ref: 40 } },
    { id: "rad", prim: "loss", in: { s: "@bath_temp" },
      params: { mode: "radiative", epsA: 1.6e-9, ref: 30 } },
    { id: "melt_rate", prim: "custom_fn", when: "phase == meltdown",
      in: { P: "@power_kw" },
      expr: "(eta_el*P*1000 - loss_melt_w) / (m_kg*dh_melt)" },
    { id: "melt", prim: "accumulator", when: "phase == meltdown",
      in: { rate: "@melt_rate" }, out: "x_melt", params: { min: 0, max: 1 } },
    { id: "net_rate", prim: "custom_fn", when: "phase in [superheat, hold, tap_p]",
      in: { P: "@power_kw", cond: "@cond", rad: "@rad" },
      expr: "(eta_el*P*1000 - cond - rad) / (m_kg*cp_l)" },
    { id: "heat", prim: "accumulator", when: "phase in [superheat, hold, tap_p]",
      in: { rate: "@net_rate" }, out: "bath_temp", params: { min: 0, max: 1600 } },
    { id: "meltset", prim: "custom_fn", on: "melt_complete", expr: "1150", out: "bath_temp" },
    { id: "tapfire", prim: "threshold", in: { x: "@tilt_angle" },
      params: { gt: 10, hyst: 2 }, emit: "tap" },
    { id: "liq", prim: "custom_fn", in: { C: "@C", Si: "@Si" },
      expr: "1569 - 97.3*(C + Si/4)", out: "liquidus" },
    { id: "energy", prim: "accumulator", in: { rate: "@power_kw" }, out: "kwh_metered" },
  ],
  outputs: [
    { from: "bath_temp", publish_as: "foundry.model.bath_temperature_estimate", tier: "CALCULATED" },
    { from: "x_melt", publish_as: "foundry.model.melt_fraction", tier: "ESTIMATED" },
    { from: "liquidus", publish_as: "foundry.model.liquidus_estimate", tier: "CALCULATED" },
  ],
};

const ENT_FURNACE: EntityDef = {
  id: "ent-furnace",
  templateId: "foundry.induction_furnace",
  class: "EQUIPMENT",
  lotOp: "TRANSFORM",
  iconKey: "flame",
  oneLine: "Melts the charge to molten iron with the power of an induction coil.",
  flowMode: "batch",
  ratedProperties: [
    { name: "capacity_kg", value: 1500, unit: "kg" },
    { name: "rated_power_kw", value: 500, unit: "kW" },
    { name: "melt_loss_pct", value: 2.0, unit: "%" },
    { name: "melt_time_min", value: 50, unit: "min" },
  ],
  priors: [
    // equipment/02-furnace.md priors table (machine-named; SI-quoted so the
    // bridge's conversion table passes them through unchanged).
    { name: "eta_el", value: 0.8 }, // electrical coupling efficiency (fraction)
    { name: "m_kg", value: 1000, unit: "kg" }, // worked-heat mass
    { name: "cp_l", value: 880, unit: "J/(kg*K)" }, // liquid-iron cp (quoted 0.88 kJ/(kg*K))
    { name: "dh_melt", value: 1.0125e6, unit: "J/kg" }, // 0.70*(1150-25)+225 kJ/kg
    { name: "loss_melt_w", value: 22000, unit: "W" }, // mean meltdown loss (lining absorption)
    { name: "t_set", value: 1500, unit: "°C" }, // tap target
  ],
  behavior: FURNACE_BEHAVIOR,
  behaviorVersion: 1,
  inputPorts: [{ id: "in_charge", name: "furnace_charge", materialClass: "ferrous_charge", flowMode: "batch" }],
  outputPorts: [
    { id: "out_metal", name: "molten_metal", materialClass: "molten_iron", flowMode: "batch" },
    { id: "out_slag", name: "slag", materialClass: "waste" },
  ],
  parameters: [
    { name: "bath_temperature", unit: "°C", value: 1438, range: [1400, 1500], dataKind: "analog_scalar", paramClass: "temperature" },
    { name: "power_kw", unit: "kW", value: 415, range: [350, 480], dataKind: "analog_scalar" },
    { name: "hold_time", unit: "min", value: 15, range: [8, 25], dataKind: "analog_scalar" },
    { name: "superheat", unit: "Δ°C", value: 88, range: [60, 110], dataKind: "analog_scalar", paramClass: "temperature" },
  ],
  boundarySignals: [
    { id: "sig_melt_start", name: "melt_start", purpose: "cycle_start", dataKind: "boolean_state" },
    { id: "sig_tap", name: "tap", purpose: "cycle_end", dataKind: "boolean_state" },
  ],
  trackedOutput: {
    tracked: true,
    idPrefix: "",
    label: "Heat",
    isPart: false,
    qc: { tests: ["carbon", "carbon_equivalent"], defects: ["off_chemistry"] },
  },
};

const ENT_SPECTROMETER: EntityDef = {
  id: "ent-spectro",
  templateId: "foundry.oes_spectrometer",
  class: "INSTRUMENT",
  lotOp: "PASSTHROUGH",
  iconKey: "microscope",
  oneLine: "An optical emission spectrometer that reads the melt chemistry.",
  inputPorts: [{ id: "in_sample", name: "metal_sample", materialClass: "molten_iron" }],
  outputPorts: [{ id: "out_sample", name: "analyzed_sample", materialClass: "molten_iron" }],
  parameters: [
    { name: "carbon", unit: "%", value: 3.12, range: [3.0, 3.3], dataKind: "analog_percentage", paramClass: "chemistry" },
    { name: "silicon", unit: "%", value: 2.05, range: [1.8, 2.3], dataKind: "analog_percentage", paramClass: "chemistry" },
    { name: "sulfur", unit: "%", value: 0.022, range: [0.01, 0.05], dataKind: "analog_percentage", paramClass: "chemistry" },
    { name: "carbon_equivalent", unit: "%", value: 4.1, range: [3.9, 4.3], dataKind: "derived_kpi", paramClass: "chemistry" },
    // Every OES reads these on the same spark; until 2026-09-24 manganese and phosphorus
    // lived only in the pack YAML (restored_sensors) and were never provisioned, and the
    // residuals were not in the catalog at all. Generic grey/ductile ranges, not a plant's.
    { name: "manganese", unit: "%", value: 0.35, range: [0.1, 0.9], dataKind: "analog_percentage", paramClass: "chemistry" },
    { name: "phosphorus", unit: "%", value: 0.03, range: [0.005, 0.1], dataKind: "analog_percentage", paramClass: "chemistry" },
    { name: "chromium", unit: "%", value: 0.05, range: [0, 0.2], dataKind: "analog_percentage", paramClass: "chemistry" },
    { name: "nickel", unit: "%", value: 0.05, range: [0, 0.5], dataKind: "analog_percentage", paramClass: "chemistry" },
    { name: "molybdenum", unit: "%", value: 0.01, range: [0, 0.1], dataKind: "analog_percentage", paramClass: "chemistry" },
    { name: "copper", unit: "%", value: 0.3, range: [0, 1.0], dataKind: "analog_percentage", paramClass: "chemistry" },
    { name: "titanium", unit: "%", value: 0.02, range: [0, 0.05], dataKind: "analog_percentage", paramClass: "chemistry" },
    { name: "vanadium", unit: "%", value: 0.01, range: [0, 0.05], dataKind: "analog_percentage", paramClass: "chemistry" },
    { name: "tin", unit: "%", value: 0.02, range: [0, 0.1], dataKind: "analog_percentage", paramClass: "chemistry" },
    { name: "aluminium", unit: "%", value: 0.01, range: [0, 0.05], dataKind: "analog_percentage", paramClass: "chemistry" },
    { name: "lead", unit: "%", value: 0.002, range: [0, 0.005], dataKind: "analog_percentage", paramClass: "chemistry" },
    { name: "cerium", unit: "%", value: 0.005, range: [0, 0.02], dataKind: "analog_percentage", paramClass: "chemistry" },
    { name: "bismuth", unit: "%", value: 0.001, range: [0, 0.01], dataKind: "analog_percentage", paramClass: "chemistry" },
  ],
};

const ENT_SAND_MIXER: EntityDef = {
  id: "ent-sand-mixer",
  templateId: "foundry.sand_mixer",
  class: "EQUIPMENT",
  lotOp: "TRANSFORM",
  iconKey: "cog",
  oneLine: "Mixes silica, return sand, bentonite and water into green sand.",
  flowMode: "batch",
  ratedProperties: [
    { name: "capacity_kg", value: 1500, unit: "kg" },
    { name: "rated_power_kw", value: 45, unit: "kW" },
    { name: "system_sand_mass_kg", value: 120000, unit: "kg" },
  ],
  inputPorts: [
    { id: "in_silica", name: "silica_sand", materialClass: "silica_sand" },
    { id: "in_return", name: "return_sand", materialClass: "return_sand" },
    { id: "in_water", name: "water", materialClass: "water" },
    { id: "in_bentonite", name: "bentonite", materialClass: "bentonite" },
    { id: "in_seacoal", name: "sea_coal", materialClass: "sea_coal" },
  ],
  outputPorts: [{ id: "out_sand", name: "mixed_sand", materialClass: "green_sand" }],
  parameters: [
    { name: "moisture_content", unit: "%", value: 3.92, range: [3.0, 4.2], dataKind: "analog_percentage", paramClass: "sand_property" },
    { name: "compactibility", unit: "%", value: 42, range: [35, 50], dataKind: "analog_percentage", paramClass: "sand_property" },
    { name: "sand_temperature", unit: "°C", value: 34, range: [25, 45], dataKind: "analog_scalar", paramClass: "temperature" },
    // Return sand entering the muller: a hot input is the usual cause of sand that
    // can't be controlled at the muller (moisture flashes off, compactibility drifts).
    { name: "sand_temp_in", unit: "°C", value: 40, range: [20, 60], dataKind: "analog_scalar", paramClass: "temperature" },
    { name: "motor_load", unit: "%", value: 70, range: [0, 100], dataKind: "analog_percentage", paramClass: "other" },
  ],
  trackedOutput: {
    tracked: true,
    idPrefix: "",
    label: "Sand batch",
    isPart: false,
    qc: {
      tests: ["moisture_content", "compactibility"],
      defects: ["high_moisture", "low_compactibility"],
    },
  },
};

const ENT_MOULDING: EntityDef = {
  id: "ent-moulding",
  templateId: "foundry.moulding_machine_disa",
  class: "EQUIPMENT",
  lotOp: "SPLIT",
  iconKey: "cog",
  oneLine: "A DISA moulding line that makes moulds from green sand and a pattern.",
  // Grey-iron template defaults (B0). flask_area_m2 × pack depth × density is
  // the honest box-sand-mass derivation (heat-sink/mass-balance term, NOT a
  // cooling driver).
  //
  // CORRECTED 2026-08-02 (WS-B#6/#8): this comment used to claim
  // "mould_sand_mass_kg is metal-instanced at plant build (grey 150 / ductile
  // 170)". It is not, and never was — generatePlant copies a template's
  // ratedProperties[] verbatim; the string "170" appears nowhere in the plant
  // code. Every plant, ductile included, gets the 150 below. The ONLY rated key
  // the builder resolves per metal is flow_rate_kg_per_min (WS-B#6,
  // generatePlant.ts POUR_FLOW_RATE_KG_PER_MIN), and a ductile mould sand mass
  // is a dossier-lock decision, not a comment.
  //
  // cavities_per_box HERE is the MAXIMUM this box accepts — a machine limit, NOT
  // the heat's fan-out (WS-B#4 / council ruling (d) 2026-08-02, and the manifest's
  // second cavities_per_box row, Class=NAMEPLATE_CONTROL). The number of castings
  // actually poured per box comes from the PATTERN the heat ran
  // (recipe.pattern_cavities count), falling back to the POUR entity's rating —
  // a tool changes several times a shift and nobody opens this panel at a
  // changeover. Editing the value below changes no heat, by design; the emitter's
  // negative control asserts exactly that.
  ratedProperties: [
    { name: "mould_sand_mass_kg", value: 150, unit: "kg" },
    { name: "flask_area_m2", value: 0.35, unit: "m²" },
    { name: "cavities_per_box", value: 2 },
  ],
  inputPorts: [
    { id: "in_sand", name: "mixed_sand", materialClass: "green_sand" },
    { id: "in_pattern", name: "pattern", materialClass: "pattern" },
  ],
  outputPorts: [{ id: "out_moulds", name: "moulds", materialClass: "green_sand_mould" }],
  parameters: [
    { name: "squeeze_pressure", unit: "bar", value: 8.1, range: [7, 9], dataKind: "analog_scalar" },
    { name: "mould_hardness", unit: "AFS", value: 82, range: [70, 95], dataKind: "analog_scalar" },
    { name: "mould_count", value: 102, dataKind: "count_scalar" },
    // The line rate. Lags between muller, pour and shakeout scale with it, so it is
    // what turns a line's mould positions into times.
    { name: "moulds_per_hour", unit: "moulds/h", value: 300, range: [0, 500], dataKind: "analog_scalar", paramClass: "rate" },
  ],
  boundarySignals: [
    { id: "sig_mould_made", name: "mould_made", purpose: "pulse", dataKind: "count_scalar" },
  ],
  trackedOutput: {
    tracked: true,
    idPrefix: "MO",
    label: "Mould",
    isPart: true,
    qc: { tests: ["mould_hardness"], defects: ["low_hardness", "sand_inclusion"] },
  },
};

const ENT_POUR: EntityDef = {
  id: "ent-pour",
  templateId: "foundry.auto_pour_station",
  class: "EQUIPMENT",
  lotOp: "SPLIT",
  iconKey: "cog",
  oneLine: "A pouring machine that pours the molten metal into the empty moulds.",
  // WS-B#9b — DOSSIER ROW DELIBERATELY NOT AUTHORED (01-melt-line.md:241, "Ladle
  // capacity | kg | 300-1500 (STD 1500) [200-5000]"). Authoring it here was tried and
  // the reachability gate refused it: `capacity_kg` is not in POUR_FIELDS, so the
  // number would be persisted on every pour station and editable nowhere. Listing it
  // would also make this the FIFTH distinct meaning of `capacity_kg` in the manifest
  // (charge-prep batch mass · furnace holding · aux-bulk batch mass · treatment pot ·
  // and now a pour ladle) — and `emitTrackedBulkLot` reads that key as a per-heat
  // BATCH MASS (genealogy_newmodel.go:4062). One name carrying five quantities is the
  // collision WS-B#7 spent a task removing, so this waits on the capacity-semantics
  // decision it shares with the sand silos (see the WS-B#9b block in _STATUS).
  inputPorts: [
    { id: "in_metal", name: "molten_metal", materialClass: "molten_iron" },
    { id: "in_moulds", name: "moulds", materialClass: "green_sand_mould" },
    { id: "in_inoculant", name: "inoculant", materialClass: "alloy_material" },
  ],
  outputPorts: [{ id: "out_poured", name: "poured_moulds", materialClass: "poured_mould" }],
  parameters: [
    { name: "pour_temperature", unit: "°C", value: 1388, range: [1370, 1410], dataKind: "analog_scalar" },
    { name: "pour_speed", unit: "kg/s", value: 2.5, range: [1.5, 3.5], dataKind: "analog_scalar" },
    { name: "inoculant_addition", unit: "%", value: 0.28, range: [0.15, 0.4], dataKind: "analog_percentage" },
  ],
  boundarySignals: [
    { id: "sig_pour_start", name: "pour_start", purpose: "cycle_start", dataKind: "boolean_state" },
    { id: "sig_casting_poured", name: "casting_poured", purpose: "pulse", dataKind: "count_scalar" },
  ],
};

const ENT_COOLING: EntityDef = {
  id: "ent-cooling",
  templateId: "foundry.cooling_line",
  class: "TRANSPORT",
  lotOp: "PASSTHROUGH",
  iconKey: "truck",
  oneLine: "A conveyor cooling line where the poured moulds solidify on the way to shakeout.",
  flowMode: "discrete_unit",
  // WS-B#8 (2026-08-02): rated `cooling_time 45 min` deleted. It was not a
  // registry key (so it reached nothing) and the identical number, same unit,
  // is the `cooling_time` PARAMETER below — the live home, and the correct one:
  // cooling time is an operational duration, not a nameplate.
  // WS-B#7 (2026-08-02): this row used to be `capacity_kg` with unit "moulds" —
  // one registry name meaning a batch MASS on the charge bay / furnace / muller
  // and a mould COUNT here. It is now the dossier's own key
  // (equipment-dossier/03-mould-shakeout.md:429 "Line capacity (WIP) · moulds ·
  // 90 [30-300] · mould-count WIP, NOT capacity_kg"), whose name carries its
  // unit. NOTE the rename was only half the fix: the emitter discarded declared
  // units at parse, so it could not have told 90 moulds from 90 kg — see
  // ratedKgOr in genealogy_newmodel.go, which now refuses a capacity_kg that is
  // not declared in kilograms. Nothing reads this value; it is a nameplate.
  ratedProperties: [{ name: "line_capacity_moulds", value: 90, unit: "moulds" }],
  inputPorts: [{ id: "in_poured", name: "poured_moulds", materialClass: "poured_mould", flowMode: "discrete_unit" }],
  outputPorts: [{ id: "out_cooled", name: "cooled_moulds", materialClass: "cooled_mould", flowMode: "discrete_unit" }],
  parameters: [
    { name: "cooling_time", unit: "min", value: 45, range: [35, 55], dataKind: "analog_scalar" },
  ],
  boundarySignals: [
    { id: "sig_shakeout", name: "shakeout_start", purpose: "cycle_end", dataKind: "boolean_state" },
  ],
};

const ENT_SHAKEOUT: EntityDef = {
  id: "ent-shakeout",
  templateId: "foundry.shakeout",
  class: "EQUIPMENT",
  lotOp: "SPLIT",
  iconKey: "cog",
  oneLine: "Shakeout, which separates the raw castings from the used return sand.",
  inputPorts: [{ id: "in_cooled", name: "cooled_moulds", materialClass: "cooled_mould" }],
  outputPorts: [
    { id: "out_castings", name: "raw_castings", materialClass: "raw_casting" },
    { id: "out_sand", name: "return_sand", materialClass: "return_sand" },
  ],
  parameters: [
    { name: "vibration_frequency", unit: "rpm", value: 1150, range: [900, 1400], dataKind: "analog_scalar", paramClass: "rate" },
    // The return sand leaving the shakeout: the heat load the cooler must remove.
    { name: "discharge_sand_temperature", unit: "°C", value: 90, range: [40, 200], dataKind: "analog_scalar", paramClass: "temperature" },
    { name: "water_add_rate", unit: "L/min", value: 20, range: [0, 100], dataKind: "analog_scalar", paramClass: "rate" },
    { name: "water_pressure", unit: "bar", value: 3, range: [0, 8], dataKind: "analog_scalar", paramClass: "other" },
  ],
  // The casting producer: shakeout separates the raw casting from spent sand,
  // so `raw_casting` (out_castings) is its tracked output (mirrors the
  // "Cooling + Shakeout" cast stage in packs/foundry/output-stages.yaml).
  trackedOutput: {
    tracked: true,
    idPrefix: "CA",
    label: "Casting",
    isPart: true,
    qc: {
      tests: ["visual", "xray"],
      defects: ["porosity", "shrinkage", "misrun", "cold_shut"],
    },
  },
};

const ENT_QC: EntityDef = {
  id: "ent-qc",
  templateId: "foundry.visual_qc_station",
  class: "STATION",
  lotOp: "PASSTHROUGH",
  iconKey: "clipboard",
  oneLine: "Inspects each casting by eye and by non-destructive testing (NDT), records its defects, and accepts or rejects it.",
  inputPorts: [{ id: "in_castings", name: "raw_castings", materialClass: "raw_casting" }],
  outputPorts: [
    { id: "out_accepted", name: "accepted_castings", materialClass: "finished_casting" },
    { id: "out_rejected", name: "rejected_castings", materialClass: "raw_casting" },
  ],
  parameters: [
    { name: "defect_count", value: 4, dataKind: "count_scalar" },
    { name: "rejection_rate", unit: "%", value: 3.9, dataKind: "analog_percentage" },
  ],
};

const ENT_TENSILE: EntityDef = {
  id: "ent-tensile",
  templateId: "foundry.tensile_testing_machine",
  class: "INSTRUMENT",
  lotOp: "PASSTHROUGH",
  iconKey: "microscope",
  oneLine: "A universal testing machine that pulls a cast test bar to break it and measure its tensile strength.",
  inputPorts: [{ id: "in_bars", name: "test_bars", materialClass: "test_specimen" }],
  outputPorts: [{ id: "out_bars", name: "tested_bars", materialClass: "test_specimen" }],
  parameters: [
    { name: "uts", unit: "MPa", value: 242, range: [150, 350], dataKind: "analog_scalar" },
    { name: "elongation", unit: "%", value: 2.4, range: [0.5, 5], dataKind: "analog_percentage" },
  ],
  trackedOutput: {
    tracked: true,
    idPrefix: "TS",
    label: "Specimen",
    isPart: true,
    qc: { tests: ["tensile_strength"], defects: ["low_strength"] },
  },
};

// Versatile sand testers — all share input/output shape
// A carve-out for destructive bench testing — economically an OUTPUT leaving
// the mass flow, not more of the circulating green_sand it was drawn from
// (classes.yaml's test_specimen notes: "Separately-cast test bars OR SAND
// SAMPLES drawn for lab testing"). Mirrors ENT_QC's out_accepted
// (finished_casting) → tensile's in_bars (test_specimen) wire: the SOURCE
// port upstream keeps its real class, only this sample inlet/outlet is
// test_specimen (#698).
const versatileSandTester = (
  id: string,
  templateId: string,
  name: string,
  param: ParameterDef,
): EntityDef => ({
  id,
  templateId,
  class: "INSTRUMENT",
  lotOp: "PASSTHROUGH",
  iconKey: "microscope",
  oneLine: `${name}, a bench test of one green sand property.`,
  inputPorts: [{ id: "in_sample", name: "green_sand_sample", materialClass: "test_specimen" }],
  outputPorts: [{ id: "out_sample", name: "analyzed_sample", materialClass: "test_specimen" }],
  parameters: [param],
});

const ENT_MOISTURE_TESTER = versatileSandTester(
  "ent-moisture-tester",
  "foundry.versatile_moisture_tester",
  "Digital Moisture Tester",
  { name: "moisture_content", unit: "%", value: 3.88, range: [2, 6], dataKind: "analog_percentage" },
);

const ENT_VCCD = versatileSandTester(
  "ent-vccd",
  "foundry.versatile_vccd_i",
  "Versatile VCCD-I",
  { name: "compactibility", unit: "%", value: 42, range: [30, 55], dataKind: "analog_percentage" },
);

const ENT_VUD = versatileSandTester(
  "ent-vud",
  "foundry.versatile_vud_iii",
  "Versatile VUD-III",
  { name: "green_compression_strength", unit: "g/cm²", value: 1240, range: [800, 1800], dataKind: "analog_scalar" },
);

const ENT_VPD = versatileSandTester(
  "ent-vpd",
  "foundry.versatile_vpd_ii",
  "Versatile VPD-II",
  { name: "permeability_number", unit: "AFS", value: 135, range: [80, 180], dataKind: "analog_scalar" },
);

// The Versatile VMBD (methylene blue clay tester): one result per completed test, sent by the VMBD app's results feed
// through GatewayPro on the VMBD's own Pi (docs/design/2026-10-18_vmbd-results-to-forge.md). Only active_clay_content
// is a sand property the analysis reads (the raw methylene blue result); the calibrated value is shown, not analysed,
// and is named so the analysis's active clay rule never matches it.
const ENT_VMBD: EntityDef = {
  id: "ent-vmbd",
  templateId: "foundry.versatile_vmbd",
  class: "INSTRUMENT",
  feedDriver: "results-feed",
  lotOp: "PASSTHROUGH",
  iconKey: "microscope",
  oneLine: "A methylene blue test of the active clay in the sand, with one result per sample.",
  inputPorts: [{ id: "in_sample", name: "green_sand_sample", materialClass: "test_specimen" }],
  outputPorts: [{ id: "out_sample", name: "analyzed_sample", materialClass: "test_specimen" }],
  parameters: [
    { name: "active_clay_content", unit: "%", value: 8, range: [5, 13], dataKind: "analog_percentage", paramClass: "sand_property" },
    { name: "calibrated_clay", unit: "%", range: [5, 13], dataKind: "analog_percentage", paramClass: "other" },
    { name: "mb_solution_volume", unit: "mL", dataKind: "analog_scalar", paramClass: "other" },
    { name: "dot_count", dataKind: "count_scalar", paramClass: "count" },
    { name: "test_duration", unit: "s", range: [0, 7500], dataKind: "analog_scalar", paramClass: "other" },
    { name: "cycle_number", dataKind: "count_scalar", paramClass: "count" },
    { name: "instrument_ok", dataKind: "boolean_state", paramClass: "state" },
  ],
};

const ENT_VMHD: EntityDef = {
  id: "ent-vmhd",
  templateId: "foundry.versatile_vmhd",
  class: "INSTRUMENT",
  lotOp: "PASSTHROUGH",
  iconKey: "microscope",
  oneLine: "A mould hardness tester that measures how hard the compacted mould surface is.",
  inputPorts: [{ id: "in_mould", name: "mould_sample", materialClass: "green_sand_mould" }],
  outputPorts: [{ id: "out_mould", name: "analyzed_sample", materialClass: "green_sand_mould" }],
  parameters: [{ name: "mould_hardness", unit: "AFS", value: 82, range: [60, 95], dataKind: "analog_scalar" }],
};

// --- Core line archetypes (Phase 2 / GAP-1, per 07-template-build-spec.md) ---
// The cold-box core line, absent from the original spine. Additive: defined +
// exported for the catalog + the coming standard-plant graph; valid MaterialClass
// on every port so connection validation holds.
const ENT_CORE_MIXER: EntityDef = {
  id: "ent-core-mixer",
  templateId: "foundry.core_sand_mixer",
  class: "EQUIPMENT",
  lotOp: "TRANSFORM",
  iconKey: "cog",
  oneLine: "A continuous cold-box mixer that coats silica sand with the two parts of the polyurethane resin.",
  line: "core",
  // WS-B#9b: dossier 02-sand-core.md:267 (22 kW [11-75]) and :268 (bench life 30 min
  // [10-90]) — both STATED values, matching the sibling foundry.std_core_sand_mixer.
  //
  // THE DOSSIER'S THIRD NUMBER FOR THIS MACHINE IS DELIBERATELY *NOT* AUTHORED.
  // 02-sand-core.md:266 prescribes `flow_rate_t_per_h` = 20 t/h, while this template's
  // own `throughput` parameter below ships 1500 kg/h [500-3000] = **1.5 t/h** — a 13x
  // disagreement. Authoring the rated 20 would put both numbers inside one template
  // and let a reader pick either; one of the two is simply wrong for this machine
  // class. That is a dossier-owner reconciliation, and inventing a compromise (or
  // silently trusting the dossier over the shipped tag range) is exactly the kind of
  // quiet number this build exists to stop. Left MISSING, with the contradiction
  // written down — see the WS-B#9b block in _STATUS.
  ratedProperties: [
    { name: "rated_power_kw", value: 22, unit: "kW" },
    { name: "bench_life_min", value: 30, unit: "min" },
  ],
  inputPorts: [
    { id: "in_sand", name: "core_silica_sand", materialClass: "silica_sand" },
    { id: "in_resin", name: "resin_part1_2", materialClass: "resin_pur" },
    { id: "in_amine", name: "amine_catalyst", materialClass: "amine_catalyst" },
  ],
  outputPorts: [{ id: "out_coated", name: "coated_core_sand", materialClass: "core_sand" }],
  parameters: [
    { name: "total_resin_pct", unit: "%", value: 1.1, range: [0.8, 1.6], dataKind: "analog_percentage" },
    { name: "throughput", unit: "kg/h", value: 1500, range: [500, 3000], dataKind: "analog_scalar" },
  ],
  category: "STD",
};
const ENT_CORE_SHOOTER: EntityDef = {
  id: "ent-core-shooter",
  templateId: "foundry.core_shooter",
  class: "EQUIPMENT",
  lotOp: "TRANSFORM",
  iconKey: "cog",
  oneLine: "A cold-box core shooter that blows coated sand into the core box and hardens the core with amine gas.",
  line: "core",
  // WS-B#9b: dossier 02-sand-core.md:638 (1-8, prior 4) — the same count the catalog
  // sibling foundry.std_core_shooter has always shipped, and which WS-B#9a gave its
  // own manifest row (cavities_per_box @ roleCoreShop, NAMEPLATE_CONTROL: cavities
  // this box fills per SHOT — not the retired cores_per_mould). Unit-less: a count.
  //
  // `rated_power_kw` (02:640) is deliberately NOT authored: the dossier gives only a
  // band "15-55" with no default, and a midpoint would be an invented nameplate. That
  // the catalog sibling ships 30 kW is not evidence — that number has no dossier row
  // behind it either.
  ratedProperties: [{ name: "cavities_per_box", value: 4 }],
  inputPorts: [
    { id: "in_coated", name: "coated_core_sand", materialClass: "core_sand" },
    { id: "in_corebox", name: "corebox", materialClass: "pattern" },
  ],
  outputPorts: [{ id: "out_core", name: "green_core", materialClass: "cold_box_core" }],
  parameters: [
    { name: "shoot_pressure", unit: "bar", value: 5, range: [4, 6], dataKind: "analog_scalar" },
    { name: "gas_time", unit: "s", value: 4, range: [2, 10], dataKind: "analog_scalar" },
    { name: "core_tensile", unit: "N/cm2", value: 280, range: [200, 350], dataKind: "analog_scalar" },
  ],
  category: "STD",
};
const ENT_CORE_COATING: EntityDef = {
  id: "ent-core-coating",
  templateId: "foundry.core_coating",
  class: "STATION",
  lotOp: "PASSTHROUGH",
  iconKey: "layers",
  oneLine: "Coats the core with a zircon or graphite refractory wash, by dipping or flow coating, before it dries.",
  line: "core",
  inputPorts: [{ id: "in_core", name: "green_core", materialClass: "cold_box_core" }],
  outputPorts: [{ id: "out_core", name: "coated_core", materialClass: "cold_box_core" }],
  parameters: [{ name: "coating_baume", unit: "Be", value: 42, range: [35, 48], dataKind: "analog_scalar" }],
  category: "STD",
};
const ENT_DRYING_OVEN: EntityDef = {
  id: "ent-drying-oven",
  templateId: "foundry.drying_oven",
  class: "EQUIPMENT",
  lotOp: "PASSTHROUGH",
  iconKey: "flame",
  oneLine: "A core drying oven that drives off the coating carrier before the core is set.",
  line: "core",
  inputPorts: [{ id: "in_core", name: "coated_core", materialClass: "cold_box_core" }],
  outputPorts: [{ id: "out_core", name: "dried_core", materialClass: "cold_box_core" }],
  parameters: [
    { name: "oven_temp", unit: "C", value: 150, range: [120, 180], dataKind: "analog_scalar" },
    { name: "dwell", unit: "min", value: 30, range: [20, 60], dataKind: "analog_scalar" },
  ],
  category: "STD",
};
const ENT_CORE_SETTING: EntityDef = {
  id: "ent-core-setting",
  templateId: "foundry.core_setting",
  class: "STATION",
  lotOp: "PASSTHROUGH",
  iconKey: "layers",
  oneLine: "Sets each dried core into the mould cavity before the box is closed.",
  line: "core",
  inputPorts: [
    { id: "in_mould", name: "green_mould", materialClass: "green_sand_mould" },
    { id: "in_core", name: "dried_core", materialClass: "cold_box_core" },
  ],
  outputPorts: [{ id: "out_mould", name: "cored_mould", materialClass: "green_sand_mould" }],
  // NO PARAMETERS. A `cores_per_mould` data port shipped here with value 2, range [1,8].
  // Because plantParam discards the declared nominal and the emitter DRAWS from the range,
  // that port rendered a fresh random count on every heat — measured on staging 2026-08-02,
  // three live Core Setting ops carried 0.89, 5.31 and 7.26 cores per mould, each flagged
  // `inSpec: true`. Fractional cores, presented as measurements, on a count.
  //
  // The count is now DERIVED and disclosed by the emitter (`cores_made` + `cores_basis`),
  // so a drawn port here would be a second number for one quantity — the shape WS-B#2
  // removed from the charge bay. Deleted rather than re-ranged: a random integer would
  // still be a fabricated count.
  //
  // A REAL port is still wanted here and is NOT this one: council INV §2 asks for
  // `cores_made` vs `cores_set` as MEASURED counts at the core shop, which is core-shop
  // reconciliation work, not a nameplate. (The emitter's retiredKeyGuard also withholds
  // this key on the four Core Setting entities already persisted on staging, which still
  // declare it.)
  parameters: [],
  category: "STD",
};

// --- Melt / sand-detail / finishing / tooling archetypes (Phase 2 / GAP-1) ---
const ENT_TREATMENT_LADLE: EntityDef = {
  id: "ent-treatment-ladle",
  templateId: "foundry.treatment_ladle",
  class: "EQUIPMENT",
  lotOp: "TRANSFORM",
  iconKey: "flame",
  oneLine: "A magnesium treatment ladle that makes the iron ductile, with nodular graphite. The fade starts from this moment.",
  line: "melt",
  // WS-B#9b (2026-08-03) — the same two dossier treatment-pot numbers authored on
  // foundry.mg_treatment_station in catalog/stages.ts (01-melt-line.md:293 capacity
  // 1500, :294 Mg→pour window STD 600). Both WIRED: the capacity is read by the
  // treatment-grain resolver (treatment_grain.go:206) and the window by the Mg-fade
  // model. Kept byte-identical to the catalog sibling so the demo graph and a
  // wizard-built plant cannot disagree about the same vessel.
  //
  // KNOWN CONFLICT, recorded not reconciled: the `treat_to_pour` parameter below is
  // 8 min [0-12] — its upper end (720 s) EXCEEDS this 600 s window, and the STD
  // sibling foundry.std_treatment_ladle carries the same 600 against its own
  // `fade_time` 300 s [60-600]. One of the three is wrong; which is a dossier-owner
  // call, not an authoring one.
  ratedProperties: [
    { name: "capacity_kg", value: 1500, unit: "kg" },
    { name: "mg_window_sec", value: 600, unit: "s" },
  ],
  inputPorts: [{ id: "in_metal", name: "molten_iron", materialClass: "molten_iron" }],
  outputPorts: [{ id: "out_metal", name: "treated_iron", materialClass: "molten_iron" }],
  parameters: [
    { name: "mg_addition_pct", unit: "%", value: 1.3, range: [1.0, 1.6], dataKind: "analog_percentage" },
    // WS-D / MET §3 — per-LADLE measured outcome: port + range kept, the invented nominal deleted
    // (see the same edit on foundry.mg_treatment_station in catalog/stages.ts).
    { name: "residual_mg", unit: "%", range: [0.03, 0.05], dataKind: "analog_percentage", paramClass: "chemistry" },
    { name: "treat_to_pour", unit: "min", value: 8, range: [0, 12], dataKind: "analog_scalar" },
    { name: "ladle_temp", unit: "°C", value: 1500, range: [1480, 1520], dataKind: "analog_scalar", paramClass: "temperature" },
  ],
  category: "STD",
};
const ENT_SAND_COOLER: EntityDef = {
  id: "ent-sand-cooler",
  templateId: "foundry.sand_cooler",
  class: "EQUIPMENT",
  lotOp: "PASSTHROUGH",
  iconKey: "truck",
  oneLine: "A fluidised bed sand cooler that cools the return sand before the muller.",
  line: "sand",
  // WS-B#9b: dossier 02-sand-core.md:387 ("Cooler throughput | t/h | 30 [10-100]")
  // and :388 ("Rated fan / power | kW | 30 [15-110]") — both STATED values. Two
  // nameplates: `flow_rate_t_per_h` has no Go reader at all, and installed fan power
  // reaches no heat model, so both render with the not-enforced disclosure.
  ratedProperties: [
    { name: "flow_rate_t_per_h", value: 30, unit: "t/h" },
    { name: "rated_power_kw", value: 30, unit: "kW" },
  ],
  inputPorts: [{ id: "in_hot", name: "hot_return_sand", materialClass: "return_sand" }],
  outputPorts: [{ id: "out_cool", name: "cooled_return_sand", materialClass: "return_sand" }],
  parameters: [
    { name: "sand_temp_in", unit: "°C", value: 110, range: [60, 150], dataKind: "analog_scalar", paramClass: "temperature" },
    { name: "sand_temp_out", unit: "°C", value: 42, range: [30, 45], dataKind: "analog_scalar", paramClass: "temperature" },
    { name: "moisture", unit: "%", value: 2.2, range: [0.5, 4], dataKind: "analog_percentage", paramClass: "sand_property" },
    { name: "moisture_in", unit: "%", value: 1.2, range: [0, 4], dataKind: "analog_percentage", paramClass: "sand_property" },
    { name: "motor_load", unit: "%", value: 60, range: [0, 100], dataKind: "analog_percentage", paramClass: "other" },
    { name: "water_added", unit: "L/min", value: 15, range: [0, 80], dataKind: "analog_scalar", paramClass: "rate" },
  ],
  category: "STD",
};
const ENT_SHOT_BLAST: EntityDef = {
  id: "ent-shot-blast",
  templateId: "foundry.shot_blasting",
  class: "EQUIPMENT",
  lotOp: "PASSTHROUGH",
  iconKey: "cog",
  oneLine: "Shot blasting cleans the sand and scale off the raw casting.",
  line: "finishing",
  inputPorts: [{ id: "in_casting", name: "raw_casting", materialClass: "raw_casting" }],
  outputPorts: [{ id: "out_casting", name: "blasted_casting", materialClass: "raw_casting" }],
  parameters: [{ name: "cycle_time", unit: "s", value: 180, range: [90, 360], dataKind: "analog_scalar" }],
  category: "STD",
};
const ENT_DEGATING: EntityDef = {
  id: "ent-degating",
  templateId: "foundry.de_gating",
  class: "STATION",
  lotOp: "TRANSFORM",
  iconKey: "cog",
  oneLine: "Removes the runners and risers, which go back into the charge.",
  line: "finishing",
  inputPorts: [{ id: "in_casting", name: "blasted_casting", materialClass: "raw_casting" }],
  outputPorts: [
    { id: "out_casting", name: "degated_casting", materialClass: "raw_casting" },
    // Runners/risers are RECOVERED METAL that goes back to the charge (this entity's
    // own one-line says so) — a weighed asset at ~40-55% of charge value, NOT waste.
    // Classing it "waste" costs an asset as scrap. Aligned to the canonical catalog's
    // std_degating (lib/plant/catalog/stages.ts) which already uses ferrous_metal.
    { id: "out_returns", name: "runners_risers", materialClass: "ferrous_metal" },
  ],
  parameters: [{ name: "yield_pct", unit: "%", value: 62, range: [50, 70], dataKind: "analog_percentage" }],
  category: "STD",
};
const ENT_FETTLING: EntityDef = {
  id: "ent-fettling",
  templateId: "foundry.fettling_grinding",
  class: "STATION",
  lotOp: "TRANSFORM",
  iconKey: "cog",
  oneLine: "Fettling and grinding dress the casting to the finished part.",
  line: "finishing",
  inputPorts: [{ id: "in_casting", name: "degated_casting", materialClass: "raw_casting" }],
  outputPorts: [{ id: "out_casting", name: "finished_casting", materialClass: "finished_casting" }],
  parameters: [{ name: "fettle_hr_per_t", unit: "h/t", value: 30, range: [20, 40], dataKind: "analog_scalar" }],
  category: "STD",
};
const ENT_FINAL_INSPECTION: EntityDef = {
  id: "ent-final-inspection",
  templateId: "foundry.final_inspection_ndt",
  class: "STATION",
  lotOp: "PASSTHROUGH",
  iconKey: "clipboard",
  oneLine: "The final inspection before dispatch, by eye, by non-destructive testing (NDT) and by microstructure.",
  line: "finishing",
  inputPorts: [{ id: "in_casting", name: "finished_casting", materialClass: "finished_casting" }],
  outputPorts: [{ id: "out_casting", name: "accepted_casting", materialClass: "finished_casting" }],
  parameters: [
    { name: "hardness_bhn", unit: "HB", value: 200, range: [130, 300], dataKind: "analog_scalar" },
    // WS-D / MET §3 — per-LADLE micrographic measurement: port + range kept, the invented nominal
    // deleted (see the same edit on foundry.std_final_inspection in catalog/stages.ts).
    { name: "nodularity", unit: "%", range: [85, 100], dataKind: "analog_percentage" },
  ],
  category: "STD",
};
const ENT_PATTERN_STORAGE: EntityDef = {
  id: "ent-pattern-storage",
  templateId: "foundry.pattern_storage",
  class: "STATION",
  lotOp: "PASSTHROUGH",
  iconKey: "layers",
  oneLine: "Stores the match plates and issues them to the moulding line.",
  line: "tooling",
  inputPorts: [],
  outputPorts: [{ id: "out_pattern", name: "pattern", materialClass: "pattern" }],
  parameters: [],
  category: "STD",
};

// --- Plant layout: nodes + edges --------------------------------------

// --- Sand-plant material stores (Phase 2 / GAP-1) — feed the muller MERGE ---
// Additive raw-feed sources; each PASSTHROUGH station emits one material class
// into the muller's matching input port (identity rule → connection valid).
const ENT_FRESHSAND_STORE: EntityDef = {
  id: "ent-freshsand-store",
  templateId: "foundry.fresh_sand_store",
  class: "STATION",
  lotOp: "PASSTHROUGH",
  iconKey: "layers",
  oneLine: "A store of new silica sand that tops up the system sand lost to spillage and dust.",
  line: "sand",
  // WS-B#9c (2026-08-03). Dossier 02-sand-core.md:507 "Silo capacity (each) | t |
  // silica 300 …" → 300 000 kg, authored on `storage_capacity_kg` and NOT on
  // `capacity_kg`: the latter is read as a tracked bulk lot's per-heat BATCH mass
  // (emitTrackedBulkLot), which would make this store's stock a 300-tonne sand lot
  // on a single heat. A store's capacity is a STOCK. NAMEPLATE — no reader.
  ratedProperties: [{ name: "storage_capacity_kg", value: 300000, unit: "kg" }],
  inputPorts: [],
  outputPorts: [{ id: "out_sand", name: "fresh_silica_sand", materialClass: "silica_sand" }],
  parameters: [
    { name: "make_up_pct", unit: "%", value: 3, range: [1, 8], dataKind: "analog_percentage" },
    { name: "gfn", unit: "AFS", value: 58, range: [50, 65], dataKind: "analog_scalar" },
  ],
  category: "STD",
};
const ENT_BENTONITE_STORE: EntityDef = {
  id: "ent-bentonite-store",
  templateId: "foundry.bentonite_store",
  class: "STATION",
  lotOp: "PASSTHROUGH",
  iconKey: "layers",
  oneLine: "A store of bentonite clay, the green sand binder added to each mull.",
  line: "sand",
  // WS-B#9c. Dossier 02-sand-core.md:507 "… bentonite 60" (t) → 60 000 kg on
  // `storage_capacity_kg`, never `capacity_kg` (see the fresh-sand store above).
  ratedProperties: [{ name: "storage_capacity_kg", value: 60000, unit: "kg" }],
  inputPorts: [],
  outputPorts: [{ id: "out_bentonite", name: "bentonite", materialClass: "bentonite" }],
  parameters: [{ name: "addition_pct", unit: "%", value: 0.8, range: [0.4, 1.5], dataKind: "analog_percentage" }],
  category: "STD",
};
const ENT_SEACOAL_STORE: EntityDef = {
  id: "ent-seacoal-store",
  templateId: "foundry.sea_coal_store",
  class: "STATION",
  lotOp: "PASSTHROUGH",
  iconKey: "layers",
  oneLine: "A store of sea coal, the lustrous carbon former added so the sand peels cleanly off the casting.",
  line: "sand",
  // WS-B#9c. Dossier 02-sand-core.md:507 "… sea-coal 30" (t) → 30 000 kg on
  // `storage_capacity_kg`, never `capacity_kg` (see the fresh-sand store above).
  ratedProperties: [{ name: "storage_capacity_kg", value: 30000, unit: "kg" }],
  inputPorts: [],
  outputPorts: [{ id: "out_seacoal", name: "sea_coal", materialClass: "sea_coal" }],
  parameters: [{ name: "addition_pct", unit: "%", value: 0.5, range: [0.2, 1.0], dataKind: "analog_percentage" }],
  category: "STD",
};
const ENT_WATER_ADDITION: EntityDef = {
  id: "ent-water-addition",
  templateId: "foundry.water_addition",
  class: "STATION",
  lotOp: "PASSTHROUGH",
  iconKey: "layers",
  oneLine: "Metered water addition, the muller's quickest way to move compactability.",
  line: "sand",
  inputPorts: [],
  outputPorts: [{ id: "out_water", name: "water", materialClass: "water" }],
  parameters: [{ name: "addition_pct", unit: "%", value: 2.5, range: [1.5, 3.5], dataKind: "analog_percentage" }],
  category: "STD",
};

// --- Sand return-loop detail (Phase 2 / GAP-1) — shakeout→sieve→magnet→cooler ---
const ENT_LUMP_SIEVE: EntityDef = {
  id: "ent-lump-sieve",
  templateId: "foundry.lump_crusher_sieve",
  class: "EQUIPMENT",
  lotOp: "PASSTHROUGH",
  iconKey: "cog",
  oneLine: "A lump crusher and rotary sieve that break up the metal and sand lumps in the return sand from shakeout.",
  line: "sand",
  // WS-B#9b: dossier 02-sand-core.md:449 ("Sieve throughput | t/h | 30 [10-60]") —
  // the same rating authored on the catalog sibling foundry.std_polygon_sieve.
  ratedProperties: [{ name: "flow_rate_t_per_h", value: 30, unit: "t/h" }],
  inputPorts: [{ id: "in_return", name: "hot_return_sand", materialClass: "return_sand" }],
  outputPorts: [{ id: "out_sand", name: "sieved_sand", materialClass: "return_sand" }],
  parameters: [{ name: "lumps_removed_pct", unit: "%", value: 4, range: [1, 12], dataKind: "analog_percentage" }],
  category: "STD",
};
const ENT_MAGNETIC_SEP: EntityDef = {
  id: "ent-magnetic-separator",
  templateId: "foundry.magnetic_separator",
  class: "EQUIPMENT",
  lotOp: "PASSTHROUGH",
  iconKey: "cog",
  oneLine: "A magnetic separator that pulls tramp iron (shot, fins and flash) off the return sand.",
  line: "sand",
  inputPorts: [{ id: "in_sand", name: "sieved_sand", materialClass: "return_sand" }],
  outputPorts: [{ id: "out_sand", name: "clean_return_sand", materialClass: "return_sand" }],
  parameters: [{ name: "tramp_kg_shift", unit: "kg", value: 40, range: [5, 150], dataKind: "analog_scalar" }],
  category: "STD",
};
const ENT_DUST_EXTRACTOR: EntityDef = {
  id: "ent-dust-extractor",
  templateId: "foundry.dust_extractor",
  class: "EQUIPMENT",
  lotOp: "PASSTHROUGH",
  iconKey: "cog",
  oneLine: "A dust extractor that pulls dead clay and fines off the sand system, so its active clay reads true.",
  line: "sand",
  inputPorts: [{ id: "in_sand", name: "dusty_sand", materialClass: "return_sand" }],
  outputPorts: [{ id: "out_waste", name: "collected_dust", materialClass: "waste" }],
  parameters: [
    { name: "filter_dp", unit: "mbar", value: 15, range: [5, 25], dataKind: "analog_scalar" },
    { name: "dust_kg_per_t", unit: "kg/t", value: 8, range: [3, 20], dataKind: "analog_scalar" },
  ],
  category: "STD",
};

// --- Tooling lane (Phase 2 / GAP-1) — coreboxes + heat-number stamping ---
const ENT_COREBOX_STORAGE: EntityDef = {
  id: "ent-corebox-storage",
  templateId: "foundry.corebox_storage",
  class: "STATION",
  lotOp: "PASSTHROUGH",
  iconKey: "layers",
  oneLine: "Stores the core boxes and issues them to the core shop.",
  line: "tooling",
  inputPorts: [],
  outputPorts: [{ id: "out_corebox", name: "corebox", materialClass: "pattern" }],
  parameters: [],
  category: "STD",
};
const ENT_COREBOX_INSPECTION: EntityDef = {
  id: "ent-corebox-inspection",
  templateId: "foundry.corebox_inspection",
  class: "INSTRUMENT",
  lotOp: "PASSTHROUGH",
  iconKey: "clipboard",
  oneLine: "Checks each core box for blocked vents and wear before it goes on the shooter.",
  line: "tooling",
  inputPorts: [{ id: "in_corebox", name: "corebox", materialClass: "pattern" }],
  outputPorts: [{ id: "out_corebox", name: "checked_corebox", materialClass: "pattern" }],
  parameters: [{ name: "wear_pct", unit: "%", value: 8, range: [0, 40], dataKind: "analog_percentage" }],
  category: "STD",
};
const ENT_HEATNO_STAMPING: EntityDef = {
  id: "ent-heatno-stamping",
  templateId: "foundry.heat_no_stamping",
  class: "STATION",
  lotOp: "PASSTHROUGH",
  iconKey: "clipboard",
  oneLine: "Stamps or casts in the code that traces a casting to its heat.",
  line: "tooling",
  inputPorts: [{ id: "in_casting", name: "accepted_casting", materialClass: "finished_casting" }],
  outputPorts: [{ id: "out_casting", name: "stamped_casting", materialClass: "finished_casting" }],
  parameters: [],
  category: "STD",
};

export const FOUNDRY_PLANT_NODES: PlantNode[] = [
  // Layer 0 — Charge Prep
  { id: "n_charge", instanceName: "Charge Prep Bay", position: { x: 40, y: 320 }, entity: ENT_CHARGE_PREP },

  // Layer 1 — Sand Mixer (top branch) + Furnace (main)
  // Sand-plant feed stores (Phase 2) — upstream of the muller MERGE
  { id: "n_store_fresh", instanceName: "Fresh Sand Store", position: { x: 40, y: 20 }, entity: ENT_FRESHSAND_STORE },
  { id: "n_store_bentonite", instanceName: "Bentonite Store", position: { x: 40, y: 120 }, entity: ENT_BENTONITE_STORE },
  { id: "n_store_seacoal", instanceName: "Sea-Coal Store", position: { x: 40, y: 220 }, entity: ENT_SEACOAL_STORE },
  { id: "n_store_water", instanceName: "Water Addition", position: { x: 40, y: 320 }, entity: ENT_WATER_ADDITION },
  { id: "n_sand", instanceName: "Sand Mixer A", position: { x: 280, y: 80 }, entity: ENT_SAND_MIXER },
  // Sand return-loop detail (Phase 2)
  { id: "n_sieve", instanceName: "Lump Crusher / Sieve", position: { x: 1500, y: 40 }, entity: ENT_LUMP_SIEVE },
  { id: "n_magnet", instanceName: "Magnetic Separator", position: { x: 1300, y: 40 }, entity: ENT_MAGNETIC_SEP },
  { id: "n_dust", instanceName: "Dust Extractor", position: { x: 1400, y: 160 }, entity: ENT_DUST_EXTRACTOR },
  { id: "n_furnace", instanceName: "Furnace 1", position: { x: 280, y: 320 }, entity: ENT_FURNACE },

  // Layer 2 — Sand-test instruments (top) + Spectrometer (main)
  { id: "n_moisture", instanceName: "Moisture Tester", position: { x: 540, y: -20 }, entity: ENT_MOISTURE_TESTER },
  { id: "n_vccd", instanceName: "Versatile VCCD-I", position: { x: 540, y: 60 }, entity: ENT_VCCD },
  { id: "n_vud", instanceName: "Versatile VUD-III", position: { x: 540, y: 140 }, entity: ENT_VUD },
  { id: "n_vpd", instanceName: "Versatile VPD-II", position: { x: 540, y: 220 }, entity: ENT_VPD },
  { id: "n_spectro", instanceName: "OES Spectrometer", position: { x: 540, y: 440 }, entity: ENT_SPECTROMETER },

  // Layer 3 — Moulding (main) + VMHD (top, off the moulding line)
  { id: "n_moulding", instanceName: "Moulding Line (DISA)", position: { x: 800, y: 320 }, entity: ENT_MOULDING },
  { id: "n_vmhd", instanceName: "Versatile VMHD", position: { x: 800, y: 140 }, entity: ENT_VMHD },

  // Layer 4 — Pour station
  { id: "n_pour", instanceName: "Auto-Pour Station", position: { x: 1060, y: 320 }, entity: ENT_POUR },

  // Layer 5 — Cooling
  { id: "n_cooling", instanceName: "Cooling Line", position: { x: 1320, y: 320 }, entity: ENT_COOLING },

  // Layer 6 — Shakeout
  { id: "n_shakeout", instanceName: "Shakeout", position: { x: 1580, y: 320 }, entity: ENT_SHAKEOUT },

  // Layer 7 — QC + Tensile
  { id: "n_qc", instanceName: "Visual / NDT Inspection", position: { x: 1840, y: 240 }, entity: ENT_QC },
  { id: "n_tensile", instanceName: "Tensile Testing", position: { x: 1840, y: 440 }, entity: ENT_TENSILE },

  // Phase 2 (GAP-1) — the reference plant's fuller flow. Positions are
  // placeholders: layoutFoundrySwimlanes recomputes x (dagre, process order) and
  // y (by process line), so exact coordinates here do not matter.
  { id: "n_ladle", instanceName: "Treatment Ladle (Mg)", position: { x: 400, y: 320 }, entity: ENT_TREATMENT_LADLE },
  { id: "n_core_mixer", instanceName: "Core Sand Mixer", position: { x: 200, y: 640 }, entity: ENT_CORE_MIXER },
  { id: "n_core_shooter", instanceName: "Core Shooter", position: { x: 420, y: 640 }, entity: ENT_CORE_SHOOTER },
  { id: "n_core_coating", instanceName: "Core Coating", position: { x: 620, y: 640 }, entity: ENT_CORE_COATING },
  { id: "n_core_oven", instanceName: "Core Drying Oven", position: { x: 820, y: 640 }, entity: ENT_DRYING_OVEN },
  { id: "n_core_setting", instanceName: "Core Setting", position: { x: 1000, y: 480 }, entity: ENT_CORE_SETTING },
  { id: "n_cooler", instanceName: "Sand Cooler", position: { x: 1400, y: 700 }, entity: ENT_SAND_COOLER },
  { id: "n_blast", instanceName: "Shot Blasting", position: { x: 1720, y: 320 }, entity: ENT_SHOT_BLAST },
  { id: "n_degate", instanceName: "De-gating", position: { x: 1900, y: 320 }, entity: ENT_DEGATING },
  { id: "n_fettle", instanceName: "Fettling / Grinding", position: { x: 2080, y: 320 }, entity: ENT_FETTLING },
  { id: "n_final", instanceName: "Final Inspection + NDT", position: { x: 2260, y: 320 }, entity: ENT_FINAL_INSPECTION },
  { id: "n_pattern", instanceName: "Pattern Storage", position: { x: 640, y: 820 }, entity: ENT_PATTERN_STORAGE },
  // Tooling lane (Phase 2)
  { id: "n_corebox_store", instanceName: "Corebox Storage", position: { x: 240, y: 820 }, entity: ENT_COREBOX_STORAGE },
  { id: "n_corebox_insp", instanceName: "Corebox Inspection", position: { x: 340, y: 900 }, entity: ENT_COREBOX_INSPECTION },
  { id: "n_heatstamp", instanceName: "Heat-No. Stamping", position: { x: 2460, y: 820 }, entity: ENT_HEATNO_STAMPING },
];

export const FOUNDRY_PLANT_EDGES: PlantEdge[] = [
  // Sand branch — mixer to instruments + main moulding
  { id: "e_sand_mois", source: "n_sand", target: "n_moisture", sourceHandle: "out_sand", targetHandle: "in_sample", label: "Sand sample", materialClass: "green_sand" },
  { id: "e_sand_vccd", source: "n_sand", target: "n_vccd", sourceHandle: "out_sand", targetHandle: "in_sample", label: "Sand sample", materialClass: "green_sand" },
  { id: "e_sand_vud", source: "n_sand", target: "n_vud", sourceHandle: "out_sand", targetHandle: "in_sample", label: "Sand sample", materialClass: "green_sand" },
  { id: "e_sand_vpd", source: "n_sand", target: "n_vpd", sourceHandle: "out_sand", targetHandle: "in_sample", label: "Sand sample", materialClass: "green_sand" },
  { id: "e_sand_mould", source: "n_sand", target: "n_moulding", sourceHandle: "out_sand", targetHandle: "in_sand", label: "Mixed sand", materialClass: "green_sand" },

  // Moulding → VMHD (mould sample)
  { id: "e_mould_vmhd", source: "n_moulding", target: "n_vmhd", sourceHandle: "out_moulds", targetHandle: "in_mould", label: "Mould sample", materialClass: "green_sand_mould" },

  // Main process line
  { id: "e_charge_furnace", source: "n_charge", target: "n_furnace", sourceHandle: "out_charge", targetHandle: "in_charge", label: "Furnace charge", materialClass: "ferrous_charge" },
  { id: "e_furnace_spectro", source: "n_furnace", target: "n_spectro", sourceHandle: "out_metal", targetHandle: "in_sample", label: "Metal sample", materialClass: "molten_iron" },
  { id: "e_furnace_pour", source: "n_furnace", target: "n_ladle", sourceHandle: "out_metal", targetHandle: "in_metal", label: "Tapped iron", materialClass: "molten_iron" },
  { id: "e_mould_pour", source: "n_moulding", target: "n_core_setting", sourceHandle: "out_moulds", targetHandle: "in_mould", label: "Green moulds", materialClass: "green_sand_mould" },
  { id: "e_pour_cooling", source: "n_pour", target: "n_cooling", sourceHandle: "out_poured", targetHandle: "in_poured", label: "Poured moulds", materialClass: "poured_mould" },
  { id: "e_cooling_shake", source: "n_cooling", target: "n_shakeout", sourceHandle: "out_cooled", targetHandle: "in_cooled", label: "Cooled moulds", materialClass: "cooled_mould" },
  { id: "e_shake_qc", source: "n_shakeout", target: "n_qc", sourceHandle: "out_castings", targetHandle: "in_castings", label: "Raw castings", materialClass: "raw_casting" },
  { id: "e_qc_tensile", source: "n_qc", target: "n_tensile", sourceHandle: "out_accepted", targetHandle: "in_bars", label: "Test bars", materialClass: "test_specimen" },

  // Return sand recirculation (feedback loop — dashed) via the sand cooler
  { id: "e_shake_sand", source: "n_shakeout", target: "n_sieve", sourceHandle: "out_sand", targetHandle: "in_return", label: "Hot return sand", materialClass: "return_sand", style: "dashed" },
  { id: "e_sieve_magnet", source: "n_sieve", target: "n_magnet", sourceHandle: "out_sand", targetHandle: "in_sand", label: "Sieved sand", materialClass: "return_sand", style: "dashed" },
  { id: "e_magnet_cooler", source: "n_magnet", target: "n_cooler", sourceHandle: "out_sand", targetHandle: "in_hot", label: "De-tramped sand", materialClass: "return_sand", style: "dashed" },
  { id: "e_sieve_dust", source: "n_sieve", target: "n_dust", sourceHandle: "out_sand", targetHandle: "in_sand", label: "System fines", materialClass: "return_sand", style: "dashed" },
  { id: "e_cooler_sand", source: "n_cooler", target: "n_sand", sourceHandle: "out_cool", targetHandle: "in_return", label: "Cooled return sand", materialClass: "return_sand", style: "dashed" },

  // Sand-plant feed streams into the muller MERGE (Phase 2)
  { id: "e_fresh_muller", source: "n_store_fresh", target: "n_sand", sourceHandle: "out_sand", targetHandle: "in_silica", label: "New sand", materialClass: "silica_sand" },
  { id: "e_bentonite_muller", source: "n_store_bentonite", target: "n_sand", sourceHandle: "out_bentonite", targetHandle: "in_bentonite", label: "Bentonite", materialClass: "bentonite" },
  { id: "e_seacoal_muller", source: "n_store_seacoal", target: "n_sand", sourceHandle: "out_seacoal", targetHandle: "in_seacoal", label: "Sea coal", materialClass: "sea_coal" },
  { id: "e_water_muller", source: "n_store_water", target: "n_sand", sourceHandle: "out_water", targetHandle: "in_water", label: "Water", materialClass: "water" },

  // Phase 2 (GAP-1) — melt treatment, core line, finishing tail
  { id: "e_ladle_pour", source: "n_ladle", target: "n_pour", sourceHandle: "out_metal", targetHandle: "in_metal", label: "Treated iron", materialClass: "molten_iron" },
  { id: "e_coremix_shooter", source: "n_core_mixer", target: "n_core_shooter", sourceHandle: "out_coated", targetHandle: "in_coated", label: "Coated core sand", materialClass: "core_sand" },
  { id: "e_shooter_coating", source: "n_core_shooter", target: "n_core_coating", sourceHandle: "out_core", targetHandle: "in_core", label: "Green core", materialClass: "cold_box_core" },
  { id: "e_coating_oven", source: "n_core_coating", target: "n_core_oven", sourceHandle: "out_core", targetHandle: "in_core", label: "Coated core", materialClass: "cold_box_core" },
  { id: "e_oven_coreset", source: "n_core_oven", target: "n_core_setting", sourceHandle: "out_core", targetHandle: "in_core", label: "Dried core", materialClass: "cold_box_core" },
  { id: "e_coreset_pour", source: "n_core_setting", target: "n_pour", sourceHandle: "out_mould", targetHandle: "in_moulds", label: "Cored moulds", materialClass: "green_sand_mould" },
  { id: "e_shake_blast", source: "n_shakeout", target: "n_blast", sourceHandle: "out_castings", targetHandle: "in_casting", label: "Raw castings", materialClass: "raw_casting" },
  { id: "e_blast_degate", source: "n_blast", target: "n_degate", sourceHandle: "out_casting", targetHandle: "in_casting", label: "Blasted castings", materialClass: "raw_casting" },
  { id: "e_degate_fettle", source: "n_degate", target: "n_fettle", sourceHandle: "out_casting", targetHandle: "in_casting", label: "De-gated castings", materialClass: "raw_casting" },
  { id: "e_fettle_final", source: "n_fettle", target: "n_final", sourceHandle: "out_casting", targetHandle: "in_casting", label: "Finished castings", materialClass: "finished_casting" },
  { id: "e_final_stamp", source: "n_final", target: "n_heatstamp", sourceHandle: "out_casting", targetHandle: "in_casting", label: "Accepted castings", materialClass: "finished_casting" },

  // Tooling authoring links (Phase 2, dashed) — which pattern / corebox made this
  { id: "e_pattern_mould", source: "n_pattern", target: "n_moulding", sourceHandle: "out_pattern", targetHandle: "in_pattern", label: "Pattern", materialClass: "pattern", style: "dashed" },
  { id: "e_corebox_store_insp", source: "n_corebox_store", target: "n_corebox_insp", sourceHandle: "out_corebox", targetHandle: "in_corebox", label: "Corebox", materialClass: "pattern", style: "dashed" },
  { id: "e_corebox_shooter", source: "n_corebox_insp", target: "n_core_shooter", sourceHandle: "out_corebox", targetHandle: "in_corebox", label: "Corebox", materialClass: "pattern", style: "dashed" },
];

// --- The grey-iron reference plant (the shipped template) ----------------------
//
// The plant above is a mixed foundry: its melt line magnesium-treats the iron, which makes ductile (nodular) iron.
// The template a plant installs is foundry-grey-iron (packs/foundry/plant-templates, #1213), and grey iron is not
// magnesium-treated. As grey-iron foundries run it, the tapped iron is inoculated in the ladle (an FeSi addition that
// promotes graphite and suppresses chill) and again in the stream at the pour, which the pour station already carries
// (its in_inoculant port and inoculant_addition). So the template's ladle is an inoculation ladle (Arjun, 2026-10-07:
// "replace with what could be an industry standard"). The node keeps its id, n_ladle, and its place on the melt line;
// only the vessel and the label of the iron it passes on change.
//
// The ladle is the catalog's foundry.std_inoculation_ladle (lib/plant/catalog/stages.ts), copied here because the
// template generator loads this file under plain Node, which cannot follow the catalog's "@/" imports. It adds only the
// process line, so it sits in the melt lane. plantTemplatePack.test.ts holds the copy to the catalog's definition.
// The SPA's own plant, the dev seed (scripts/gen-twin-seed.mjs) and everything that reads the treatment ladle
// (the simulator, the mock PLC, the treatment-grain resolver) keep FOUNDRY_PLANT_NODES as it is.
const ENT_GREY_IRON_INOCULATION_LADLE: EntityDef = {
  id: "ent-std-inoculation-ladle",
  templateId: "foundry.std_inoculation_ladle",
  class: "EQUIPMENT",
  lotOp: "TRANSFORM",
  iconKey: "flame",
  oneLine: "A ladle that adds a ferrosilicon inoculant just before pouring, which promotes graphite and prevents chill.",
  line: "melt",
  category: "STD",
  flowMode: "batch",
  ratedProperties: [
    { name: "capacity_kg", value: 1500, unit: "kg" },
    { name: "inoculant_addition_pct", value: 0.2, unit: "%" },
  ],
  inputPorts: [
    { id: "in_metal", name: "treated_metal", materialClass: "molten_iron", flowMode: "batch" },
    { id: "in_inoculant", name: "inoculant", materialClass: "alloy_material" },
  ],
  outputPorts: [{ id: "out_metal", name: "inoculated_metal", materialClass: "molten_iron", flowMode: "batch" }],
  parameters: [
    { name: "inoculant_addition", unit: "%", value: 0.2, range: [0.1, 0.6], dataKind: "analog_percentage" },
    { name: "pour_temperature", unit: "°C", value: 1380, range: [1340, 1450], dataKind: "analog_scalar" },
  ],
  boundarySignals: [{ id: "sig_inoc_done", name: "inoculation_complete", purpose: "cycle_end", dataKind: "boolean_state" }],
};

/** The grey-iron reference plant: FOUNDRY_PLANT_NODES with an inoculation ladle on n_ladle (see above). */
export const FOUNDRY_GREY_IRON_NODES: PlantNode[] = FOUNDRY_PLANT_NODES.map((n) =>
  n.id === "n_ladle" ? { ...n, instanceName: "Inoculation Ladle", entity: ENT_GREY_IRON_INOCULATION_LADLE } : n,
);

/** The grey-iron reference plant's connections: the ladle passes inoculated iron to the pour, not treated iron. */
export const FOUNDRY_GREY_IRON_EDGES: PlantEdge[] = FOUNDRY_PLANT_EDGES.map((e) =>
  e.id === "e_ladle_pour" ? { ...e, label: "Inoculated iron" } : e,
);

// Re-exported for the parametric plant-builder catalog (lib/plant/catalog).
// Archetype A (iron green-sand) composes these exact defs, so a default
// generatePlant() round-trips to the FOUNDRY_PLANT_NODES graph above.
export {
  ENT_TREATMENT_LADLE,
  ENT_SAND_COOLER,
  ENT_SHOT_BLAST,
  ENT_DEGATING,
  ENT_FETTLING,
  ENT_FINAL_INSPECTION,
  ENT_PATTERN_STORAGE,
  ENT_COREBOX_STORAGE,
  ENT_COREBOX_INSPECTION,
  ENT_HEATNO_STAMPING,
  ENT_FRESHSAND_STORE,
  ENT_BENTONITE_STORE,
  ENT_SEACOAL_STORE,
  ENT_WATER_ADDITION,
  ENT_LUMP_SIEVE,
  ENT_MAGNETIC_SEP,
  ENT_DUST_EXTRACTOR,
  ENT_CORE_MIXER,
  ENT_CORE_SHOOTER,
  ENT_CORE_COATING,
  ENT_DRYING_OVEN,
  ENT_CORE_SETTING,
  ENT_CHARGE_PREP,
  ENT_SAND_MIXER,
  ENT_FURNACE,
  ENT_SPECTROMETER,
  ENT_MOISTURE_TESTER,
  ENT_VCCD,
  ENT_VUD,
  ENT_VPD,
  ENT_VMBD,
  ENT_VMHD,
  ENT_MOULDING,
  ENT_POUR,
  ENT_COOLING,
  ENT_SHAKEOUT,
  ENT_QC,
  ENT_TENSILE,
};

export function findNode(id: string): PlantNode | undefined {
  return FOUNDRY_PLANT_NODES.find((n) => n.id === id);
}

/**
 * Template catalogue keyed by templateId — lets the copilot resolve
 * `foundry.induction_furnace` etc. to a usable EntityDef when accepting
 * an "add entity" proposal.
 */
export const ENTITY_TEMPLATES: Record<string, EntityDef> = Object.fromEntries(
  FOUNDRY_PLANT_NODES.map((n) => [n.entity.templateId, n.entity]),
);

export function findTemplate(templateId: string): EntityDef | undefined {
  return ENTITY_TEMPLATES[templateId];
}

/**
 * Material-class compatibility map (ported from
 * `packs/foundry/materials/classes.yaml`). A connection is valid when:
 *   - source port + target port share the same material class (identity), OR
 *   - the source material class lists the target's in compatible_with.
 */
const COMPATIBLE_WITH: Record<MaterialClass, MaterialClass[]> = {
  ferrous_metal: ["ferrous_charge"],
  ferrous_charge: ["ferrous_metal", "molten_iron"],
  // Charge-prep cost-semantics split (#677) — mirrors classes.yaml's
  // compatible_with: [ferrous_charge] for all four.
  pig_iron: ["ferrous_charge"],
  steel_scrap: ["ferrous_charge"],
  foundry_return: ["ferrous_charge"],
  casting_rejects: ["ferrous_charge"],
  molten_iron: ["ferrous_charge", "poured_mould"],
  molten_steel: ["ferrous_charge", "poured_mould"],
  molten_aluminium: ["alloy_material", "poured_mould"],
  molten_copper: ["alloy_material", "poured_mould"],
  molten_zinc: ["alloy_material", "raw_casting"],
  green_sand: ["return_sand", "green_sand_mould"],
  return_sand: ["silica_sand", "green_sand"],
  silica_sand: ["return_sand", "green_sand"],
  water: [],
  bentonite: [],
  alloy_material: ["ferrous_charge"],
  green_sand_mould: ["green_sand", "poured_mould"],
  // Per-route mould/secondary-output classes (cosmetic-correctness): each feeds the
  // pour (→ poured_mould) like green_sand_mould; no_bake from no-bake sand, permanent
  // is the reusable die that yields the casting, investment shell is built off the wax.
  no_bake_mould: ["green_sand", "poured_mould"],
  permanent_mould: ["poured_mould", "raw_casting"],
  investment_shell: ["pattern", "poured_mould"],
  poured_mould: ["green_sand_mould", "no_bake_mould", "investment_shell", "molten_iron", "cooled_mould"],
  cooled_mould: ["poured_mould", "raw_casting", "return_sand"],
  raw_casting: ["cooled_mould", "finished_casting", "test_specimen"],
  finished_casting: ["raw_casting", "test_specimen"],
  test_specimen: ["raw_casting", "finished_casting"],
  pattern: ["green_sand_mould", "investment_shell"],
  waste: [],
  // Standard green-sand foundry additions (STD archetype). sea_coal is a
  // green-sand additive; core_sand feeds the core shop (→ cold_box_core);
  // resin_pur + amine_catalyst are the cold-box binder system; cold_box_core
  // is the produced sand core set into the green-sand mould (the "core"
  // substring also makes the genealogy emitter serialize it as a part).
  sea_coal: ["green_sand"],
  core_sand: ["silica_sand", "cold_box_core"],
  resin_pur: ["core_sand"],
  amine_catalyst: ["cold_box_core"],
  cold_box_core: ["green_sand_mould"],
  // No-bake self-setting binder system (#699) — feeds the continuous mixer
  // directly (no intermediate core-sand-like product class the way the
  // cold-box pair does), so both point straight at the mixer's own output.
  no_bake_resin: ["green_sand"],
  no_bake_catalyst: ["green_sand"],
};

export function areMaterialsCompatible(a?: MaterialClass, b?: MaterialClass): boolean {
  if (!a || !b) return false; // energy (or unset) ports never material-match
  if (a === b) return true;
  return COMPATIBLE_WITH[a]?.includes(b) || COMPATIBLE_WITH[b]?.includes(a) || false;
}

/** Find an entity's port by id on either input or output side. */
export function findPort(
  entity: EntityDef,
  portId: string,
): { port: PortDef; direction: "input" | "output" } | null {
  const input = entity.inputPorts.find((p) => p.id === portId);
  if (input) return { port: input, direction: "input" };
  const output = entity.outputPorts.find((p) => p.id === portId);
  if (output) return { port: output, direction: "output" };
  return null;
}

/** "sample" or "specimen" as a word of its own: after `_`, a space or the start, never inside "resampled". */
const SAMPLE_WORD = /(^|[^a-z])(sample|specimen)s?([^a-z]|$)/i;

/**
 * Whether a connection is a SAMPLE draw-off: a scoop of sand or a test bar taken off the line to a bench instrument,
 * as against material that moves on through the process. The process map draws these light and leaves them out of
 * the ranking, so a mixer that feeds seven testers does not read as seven stages after it.
 *
 * A connection is a sample when ANY of these holds:
 *  - its material class is `test_specimen`;
 *  - its target port is an `in_sample` port, by id or by name;
 *  - its target is an INSTRUMENT and the link's label or the target port's name says "sample" or "specimen".
 *
 * The third clause is deliberately narrow. An instrument can sit IN the flow (a scale between the pour and the
 * shakeout carries the castings on), and a connection into it must still rank like any other station, so being an
 * instrument is not enough: the link has to call itself a sample. A target that is not among `nodesById` decides
 * nothing but the material class.
 */
export function isSampleEdge(edge: PlantEdge, nodesById: ReadonlyMap<string, PlantNode>): boolean {
  if (edge.materialClass === "test_specimen") return true;
  const target = nodesById.get(edge.target);
  if (!target) return false;
  const port = findPort(target.entity, edge.targetHandle)?.port;
  const portId = (edge.targetHandle ?? "").toLowerCase();
  const portName = (port?.name ?? "").toLowerCase();
  if (portId.startsWith("in_sample") || portName.startsWith("in_sample")) return true;
  if (target.entity.class !== "INSTRUMENT") return false;
  return SAMPLE_WORD.test(edge.label ?? "") || SAMPLE_WORD.test(port?.name ?? "");
}

// --- Tag → port bindings (the IoT ↔ Twin join) ------------------------

export interface TagBinding {
  portId: string;
  /** Tag identifier in the platform (Tier-1 lens). */
  tagId: string;
  /** Display name of the tag. */
  tagName: string;
  /** Gateway / device the tag publishes through. */
  gatewayName: string;
  protocol: "modbus-tcp" | "opc-ua" | "s7" | "bacnet" | "manual";
  /** Protocol-specific register or address. */
  address: string;
  /** Scaling applied at ingest (e.g. "÷ 10"). */
  scale?: string;
  unit?: string;
  /** Latest reading (display-formatted). */
  lastValue: number | string;
  /** ISO timestamp of the latest reading. */
  lastSeen: string;
  /** How the binding was created. */
  source: "auto-suggested" | "operator";
  confidence?: number;
}

export const ENTITY_BINDINGS: Record<string, TagBinding[]> = {
  n_furnace: [
    {
      portId: "out_metal",
      tagId: "tag-furn1-bath-temp",
      tagName: "furnace_1.bath_temp",
      gatewayName: "Furnace 1 PLC",
      protocol: "modbus-tcp",
      address: "40001",
      scale: "÷ 10",
      unit: "°C",
      lastValue: 1438,
      lastSeen: "2026-05-24T10:59:55.000Z",
      source: "operator",
    },
    {
      portId: "out_metal",
      tagId: "tag-furn1-power",
      tagName: "furnace_1.power_kw",
      gatewayName: "Furnace 1 PLC",
      protocol: "modbus-tcp",
      address: "40003",
      scale: "raw",
      unit: "kW",
      lastValue: 415,
      lastSeen: "2026-05-24T10:59:55.000Z",
      source: "operator",
    },
    {
      portId: "out_metal",
      tagId: "tag-furn1-tilt",
      tagName: "furnace_1.tilt_angle",
      gatewayName: "Furnace 1 PLC",
      protocol: "modbus-tcp",
      address: "40005",
      scale: "÷ 100",
      unit: "deg",
      lastValue: 0,
      lastSeen: "2026-05-24T10:59:55.000Z",
      source: "auto-suggested",
      confidence: 0.94,
    },
  ],
  n_sand: [
    {
      portId: "out_sand",
      tagId: "tag-sandmixer-moisture",
      tagName: "sand_mixer_1.moisture",
      gatewayName: "Sand Mixer A",
      protocol: "s7",
      address: "DB1.DBW0",
      scale: "÷ 100",
      unit: "%",
      lastValue: 3.92,
      lastSeen: "2026-05-24T10:59:42.000Z",
      source: "operator",
    },
    {
      portId: "out_sand",
      tagId: "tag-sandmixer-compact",
      tagName: "sand_mixer_1.compactibility",
      gatewayName: "Sand Mixer A",
      protocol: "s7",
      address: "DB1.DBW2",
      scale: "raw",
      unit: "%",
      lastValue: 42,
      lastSeen: "2026-05-24T10:59:42.000Z",
      source: "auto-suggested",
      confidence: 0.89,
    },
  ],
  n_moulding: [
    {
      portId: "out_moulds",
      tagId: "tag-disa-pressure",
      tagName: "disa.squeeze_pressure",
      gatewayName: "DISA Moulding Line",
      protocol: "opc-ua",
      address: "ns=2;s=disa.squeeze",
      unit: "bar",
      lastValue: 8.1,
      lastSeen: "2026-05-24T10:59:48.000Z",
      source: "operator",
    },
    {
      portId: "out_moulds",
      tagId: "tag-disa-count",
      tagName: "disa.mould_count",
      gatewayName: "DISA Moulding Line",
      protocol: "opc-ua",
      address: "ns=2;s=disa.count",
      lastValue: 102,
      lastSeen: "2026-05-24T10:59:48.000Z",
      source: "operator",
    },
  ],
  n_pour: [
    {
      portId: "out_poured",
      tagId: "tag-pour-temp",
      tagName: "pour_station_1.pour_temperature",
      gatewayName: "DISA Moulding Line",
      protocol: "opc-ua",
      address: "ns=2;s=pour.temp",
      unit: "°C",
      lastValue: 1388,
      lastSeen: "2026-05-24T10:59:48.000Z",
      source: "operator",
    },
    {
      portId: "out_poured",
      tagId: "tag-pour-speed",
      tagName: "pour_station_1.pour_speed",
      gatewayName: "DISA Moulding Line",
      protocol: "opc-ua",
      address: "ns=2;s=pour.speed",
      unit: "kg/s",
      lastValue: 2.5,
      lastSeen: "2026-05-24T10:59:48.000Z",
      source: "auto-suggested",
      confidence: 0.91,
    },
  ],
  n_spectro: [
    {
      portId: "out_sample",
      tagId: "tag-spectro-carbon",
      tagName: "spectro_1.carbon",
      gatewayName: "OES Spectrometer",
      protocol: "modbus-tcp",
      address: "40010",
      scale: "÷ 1000",
      unit: "%",
      lastValue: 3.12,
      lastSeen: "2026-05-24T10:46:11.000Z",
      source: "operator",
    },
    {
      portId: "out_sample",
      tagId: "tag-spectro-silicon",
      tagName: "spectro_1.silicon",
      gatewayName: "OES Spectrometer",
      protocol: "modbus-tcp",
      address: "40011",
      scale: "÷ 1000",
      unit: "%",
      lastValue: 2.05,
      lastSeen: "2026-05-24T10:46:11.000Z",
      source: "operator",
    },
  ],
  n_vud: [
    {
      portId: "out_sample",
      tagId: "tag-vud-gcs",
      tagName: "vud_iii.green_compression_strength",
      gatewayName: "Versatile Sand Tester VUD-III",
      protocol: "modbus-tcp",
      address: "40020",
      unit: "g/cm²",
      lastValue: 1240,
      lastSeen: "2026-05-24T10:58:02.000Z",
      source: "operator",
    },
  ],
  n_cooling: [
    {
      portId: "out_cooled",
      tagId: "tag-cooling-time",
      tagName: "cooling.residence_time",
      gatewayName: "DISA Moulding Line",
      protocol: "opc-ua",
      address: "ns=2;s=cool.rt",
      unit: "min",
      lastValue: 45,
      lastSeen: "2026-05-24T10:59:48.000Z",
      source: "operator",
    },
  ],
  n_shakeout: [
    {
      portId: "out_castings",
      tagId: "tag-shake-freq",
      tagName: "shakeout.vibration_frequency",
      gatewayName: "DISA Moulding Line",
      protocol: "opc-ua",
      address: "ns=2;s=shake.freq",
      unit: "rpm",
      lastValue: 1150,
      lastSeen: "2026-05-24T10:59:48.000Z",
      source: "operator",
    },
  ],
  n_qc: [
    {
      portId: "out_accepted",
      tagId: "tag-qc-defects",
      tagName: "qc.defect_count",
      gatewayName: "manual",
      protocol: "manual",
      address: "operator entry",
      lastValue: 4,
      lastSeen: "2026-05-24T11:01:12.000Z",
      source: "operator",
    },
  ],
};

